import { ArrowDown, ArrowLeft, ArrowRight, BarChart3, ChevronDown, ChevronLeft, ChevronRight, CircleHelp, Expand, Pause, Play, ScanSearch, ScanText, Settings2, Shrink, Target, Volume2, VolumeX, X, Library, Clock3, TimerReset } from 'lucide-react';
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ApiService } from '../../../services/api';
import { useReportSpeedReadingResult } from './speedReadingActivity';

import type {
  ConcreteMeaningRoundResult,
  ConcreteMeaningWord,
  GroupingSessionPrepareResponse,
  GroupingSettings,
  LibraryLevelGroup,
  MatchingRoundResult,
  MatchingWordApiResponse,
  LibraryContentItem,
  ModeConfig,
  PeripheryPosition,
  PilotSettings,
  RecommendedStoryContent,
  WordPair,
} from './speedReadingShared';

import * as shared from './speedReadingShared';

const {
  ACCENT_COLOR_OPTIONS,
  ANTONYM_INFO_PARAGRAPHS,
  BLOCKING_INFO_PARAGRAPHS,
  CAPTURE_INFO_PARAGRAPHS,
  CAPTURE_REVIEW_QUESTION,
  CAPTURE_SAMPLE_TEXT,
  CONCRETE_MEANING_INFO_PARAGRAPHS,
  FONT_FAMILY_OPTIONS,
  FONT_SIZE_LABELS,
  FONT_SIZE_OPTIONS,
  GROUPING_INFO_PARAGRAPHS,
  GROUPING_REVIEW_QUESTIONS,
  GROUPING_SAMPLE_TEXT,
  MATCHING_INFO_PARAGRAPHS,
  MULTIFOCUS_INFO_PARAGRAPHS,
  MAGIC_SPEED_INFO_PARAGRAPHS,
  METRONOME_AUDIO_SRC,
  PERIPHERY_FOCUS_INFO_PARAGRAPHS,
  PILOT_TEST_INFO_PARAGRAPHS,
  SHADOW_INFO_PARAGRAPHS,
  SYNONYM_INFO_PARAGRAPHS,
  THEME_PRIMARY_CSS,
  TEXT_READING_FONT_SIZE_CLASS,
  TEXT_READING_INFO_PARAGRAPHS,
  TEXT_READING_REVIEW_QUESTIONS,
  TEXT_READING_SAMPLE_TEXT,
  applySharedExerciseSettings,
  buildMatchingRelationLookup,
  buildMatchingRoundKey,
  fetchGroupingBootstrap,
  formatClock,
  getAdaptiveFlashWordStyle,
  getAdjacentMetronomeBpm,
  getAntonymPairPool,
  getCaptureDurationSeconds,
  getConcreteMeaningPool,
  getModeCanonicalKey,
  getModeQueryValue,
  getGroupingTimingMetrics,
  getMatchingPair,
  getMatchingRevealDuration,
  getMatchingWordTypeByModeName,
  getMetronomeIntervalMs,
  getMultifocusWordPool,
  getPilotCooldownDuration,
  getPilotRevealDuration,
  getPilotWordPool,
  getRandomPeripheryPosition,
  getSliderBackground,
  getSynonymPairPool,
  getThemeChromeButtonStyle,
  getThemeChromeTextStyle,
  getThemedBadgeStyle,
  getThemedLabelStyle,
  getThemedOptionButtonStyle,
  getThemedRangeStyle,
  getThemedReviewSelectionStyle,
  getThemedReviewSelectionTextStyle,
  getThemedSwatchStyle,
  mapMatchingApiItemsToPairs,
  normalizeDifficultyLevel,
  normalizeMatchingWordKey,
  normalizeAccentColor,
  normalizeMetronomeBpm,
  normalizePilotAnswer,
  persistSharedExerciseSettings,
  resolveAccentColor,
  shouldStopForWrongGap,
  toGroupingApiSettings,
  ModeIllustration,
  ModesGrid,
  SidebarPreparationCard,
} = shared;

const MAGIC_CHARACTER_MIN = 10;
const MAGIC_CHARACTER_MAX = 50;
const MAGIC_CHARACTER_FALLBACK = 24;
const MAGIC_HIGHLIGHT_HEIGHT_RATIO = 0.78;
const MAGIC_HIGHLIGHT_MIN_HEIGHT = 22;
const MAGIC_HIGHLIGHT_VERTICAL_NUDGE = 6;
const EXERCISE_TOOL_BUTTON_CLASS = 'inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#2f7188] transition hover:text-[#174e67]';

type ReviewPagerQuestion = {
  id: string;
  prompt: string;
  options: Array<{ key: 'A' | 'B' | 'C' | 'D' | 'E'; text: string }>;
  correctKey: 'A' | 'B' | 'C' | 'D' | 'E';
};

const ReviewQuestionsPager: React.FC<{
  questions: ReviewPagerQuestion[];
  selectedAnswers: Record<string, 'A' | 'B' | 'C' | 'D' | 'E' | undefined>;
  setSelectedAnswers: React.Dispatch<React.SetStateAction<Record<string, 'A' | 'B' | 'C' | 'D' | 'E' | undefined>>>;
  reviewSubmitted: boolean;
  activeQuestionIndex: number;
  setActiveQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  fontFamily?: string;
}> = ({
  questions,
  selectedAnswers,
  setSelectedAnswers,
  reviewSubmitted,
  activeQuestionIndex,
  setActiveQuestionIndex,
  fontFamily,
}) => {
  if (!questions.length) return null;

  const safeIndex = Math.min(Math.max(activeQuestionIndex, 0), questions.length - 1);
  const question = questions[safeIndex];
  const canGoPrevious = safeIndex > 0;
  const canGoNext = safeIndex < questions.length - 1;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-3 rounded-2xl border border-slate-200 bg-white px-5 py-4">
        <h2 className="text-[1.35rem] font-bold leading-tight text-[#174e67]" style={fontFamily ? { fontFamily } : undefined}>
          Anlama Soruları
        </h2>
        <p className="mt-1 text-xs text-slate-500">
          Soruları tek tek cevaplayın. Cevaplanan sorular alttaki göstergede işaretlenir.
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-sm font-semibold text-slate-500">Soru {safeIndex + 1}</div>
          <h4 className="mt-2 text-[clamp(1.05rem,2.2vmin,1.45rem)] font-bold leading-snug text-slate-900" style={fontFamily ? { fontFamily } : undefined}>
            {question.prompt}
          </h4>
          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
            {question.options.map((option) => {
              const isSelected = selectedAnswers[question.id] === option.key;
              const isCorrect = question.correctKey === option.key;
              const isWrongSelection = reviewSubmitted && isSelected && !isCorrect;
              const showCorrect = reviewSubmitted && isCorrect;
              const isPendingSelection = isSelected && !reviewSubmitted;

              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    if (reviewSubmitted) return;
                    setSelectedAnswers((prev) => ({ ...prev, [question.id]: option.key }));
                  }}
                  className={`rounded-xl border-2 px-4 py-3 text-left transition-all ${showCorrect
                    ? 'border-[#6acb86] bg-[#f0fbf3]'
                    : isWrongSelection
                      ? 'border-[#f28c8c] bg-[#fff3f3]'
                      : isSelected
                        ? ''
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  style={getThemedReviewSelectionStyle(isPendingSelection)}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-sm font-black ${showCorrect ? 'text-[#46b96a]' : isWrongSelection ? 'text-[#ef5350]' : 'text-slate-500'}`}
                      style={getThemedReviewSelectionTextStyle(isPendingSelection)}
                    >
                      {option.key}
                    </span>
                    <span
                      className="text-sm font-semibold leading-snug text-slate-700"
                      style={isPendingSelection ? getThemedReviewSelectionTextStyle(true) : fontFamily ? { fontFamily } : undefined}
                    >
                      {option.text}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-3 flex shrink-0 flex-col items-center gap-3 border-t border-slate-100 pt-3">
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setActiveQuestionIndex((prev) => Math.max(0, prev - 1))}
            disabled={!canGoPrevious}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-35"
            aria-label="Önceki soru"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="flex items-center justify-center gap-2">
            {questions.map((item, index) => {
              const isActive = index === safeIndex;
              const isAnswered = Boolean(selectedAnswers[item.id]);

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveQuestionIndex(index)}
                  className={`flex h-10 min-w-[2.5rem] items-center justify-center rounded-md border px-3 text-sm font-bold transition-colors ${isActive
                    ? 'border-transparent text-white shadow-sm'
                    : isAnswered
                      ? 'border-[#b9d9d6] bg-[#eefafa] text-[#23756f]'
                      : 'border-transparent bg-transparent text-slate-500 hover:bg-slate-100'
                    }`}
                  style={isActive ? { backgroundColor: 'var(--theme-primary)' } : undefined}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setActiveQuestionIndex((prev) => Math.min(questions.length - 1, prev + 1))}
            disabled={!canGoNext}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-35"
            aria-label="Sonraki soru"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const normalizeCharacterCount = (value?: number) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return MAGIC_CHARACTER_FALLBACK;
  return Math.max(MAGIC_CHARACTER_MIN, Math.min(MAGIC_CHARACTER_MAX, Math.round(parsed)));
};

const buildMagicChunks = (words: string[], characterCount: number): string[] => {
  if (!words.length) return [];

  const chunks: string[] = [];
  let currentChunkWords: string[] = [];
  let currentLength = 0;

  words.forEach((word) => {
    const nextLength = currentLength === 0 ? word.length : currentLength + 1 + word.length;
    if (currentChunkWords.length > 0 && nextLength > characterCount) {
      chunks.push(currentChunkWords.join(' '));
      currentChunkWords = [word];
      currentLength = word.length;
      return;
    }

    currentChunkWords.push(word);
    currentLength = nextLength;
  });

  if (currentChunkWords.length > 0) {
    chunks.push(currentChunkWords.join(' '));
  }

  return chunks;
};

const getTextExerciseMinimumWordCount = (readingLevel?: string | null) => {
  switch (normalizeDifficultyLevel(readingLevel)) {
    case 'İlkokul':
      return 150;
    case 'Ortaokul':
      return 250;
    case 'Lise':
      return 350;
    default:
      return 250;
  }
};

const requestExerciseFullscreen = async (container: HTMLElement | null) => {
  if (!container || typeof document === 'undefined') return;
  if (document.fullscreenElement) return;

  try {
    await container.requestFullscreen();
  } catch (error) {
    console.error('Tam ekran başlatılamadı:', error);
  }
};

const SettingsSwitchKnob: React.FC<{ checked: boolean }> = ({ checked }) => (
  <span
    className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow-[0_2px_8px_rgba(43,62,76,0.18)] transition-all ${checked ? 'left-[34px]' : 'left-1'}`}
  />
);

const SettingsToggleRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: () => void;
}> = ({ label, checked, onChange }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="flex min-h-14 items-center gap-4 px-4 py-3">
      <span className="flex-1 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</span>
      <button
        onClick={onChange}
        className={`relative h-8 w-16 shrink-0 rounded-full transition-colors ${checked ? 'bg-[#b6d2ad]' : 'bg-[#d7e1e7]'}`}
        aria-pressed={checked}
        type="button"
      >
        <SettingsSwitchKnob checked={checked} />
      </button>
    </div>
  </div>
);

const SettingsStepperRow: React.FC<{
  label: string;
  value: string;
  onDecrease: () => void;
  onIncrease: () => void;
}> = ({ label, value, onDecrease, onIncrease }) => (
  <div className="overflow-hidden rounded-xl border border-[#d9e6eb] bg-white">
    <div className="grid h-14 grid-cols-[minmax(0,1fr)_64px_120px_64px] items-center">
      <div className="border-r border-[#e7eef2] px-4 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">{label}</div>
      <button
        onClick={onDecrease}
        className="h-full border-r border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]"
        type="button"
      >
        &laquo;
      </button>
      <div className="text-center text-[15px] font-black text-[#5d7380]">{value}</div>
      <button
        onClick={onIncrease}
        className="h-full border-l border-[#e7eef2] text-xl font-black text-[#b0bec6] transition-colors hover:bg-[#f7fbfd] hover:text-[#6f8a96]"
        type="button"
      >
        &raquo;
      </button>
    </div>
  </div>
);

const computeMagicSegments = (
  progress: number,
  chars: string[],
  windowSize: number,
  metrics: Record<number, { x: number; y: number; width: number; height: number; right: number }>
): Array<{ x: number; y: number; width: number; height: number }> => {
  const totalChars = chars.length;
  if (totalChars === 0) return [];

  const safeProgress = Math.max(0, progress);
  const intStart = Math.floor(safeProgress);
  const frac = safeProgress - intStart;

  if (intStart >= totalChars) return [];

  const fractionalEnd = safeProgress + windowSize - 1;
  const clampedEnd = Math.min(totalChars - 1, fractionalEnd);
  const intEnd = Math.floor(clampedEnd);
  const fracEnd = clampedEnd - intEnd;

  let startIndex = intStart;
  let endIndex = intEnd;

  const primaryLineY = metrics[startIndex]?.y;
  if (Number.isFinite(primaryLineY)) {
    const MIN_NEXT_LINE_CHARS = 4;
    let nextLineCharCount = 0;
    let touchesNextLine = false;
    for (let i = startIndex; i <= endIndex; i += 1) {
      const m = metrics[i];
      if (!m) continue;
      if (m.y !== primaryLineY) {
        touchesNextLine = true;
        if (!/\s/.test(chars[i] || '')) nextLineCharCount += 1;
      }
    }
    if (touchesNextLine && nextLineCharCount > 0 && nextLineCharCount < MIN_NEXT_LINE_CHARS) {
      for (let i = endIndex + 1; i < totalChars; i += 1) {
        const m = metrics[i];
        if (!m) continue;
        endIndex = i;
        if (m.y !== primaryLineY && !/\s/.test(chars[i] || '')) {
          nextLineCharCount += 1;
          if (nextLineCharCount >= MIN_NEXT_LINE_CHARS) break;
        }
      }
    }
  }

  while (startIndex < endIndex && /\s/.test(chars[startIndex] || '')) startIndex += 1;
  while (endIndex > startIndex && /\s/.test(chars[endIndex] || '')) endIndex -= 1;

  const visibleChars: Array<{
    index: number; x: number; y: number; width: number; height: number; right: number;
  }> = [];
  for (let i = startIndex; i <= endIndex; i += 1) {
    const m = metrics[i];
    if (m) visibleChars.push({ index: i, ...m });
  }
  if (visibleChars.length === 0) return [];

  const lineGroups = new Map<number, typeof visibleChars>();
  visibleChars.forEach((vc) => {
    const list = lineGroups.get(vc.y) || [];
    list.push(vc);
    lineGroups.set(vc.y, list);
  });

  const sortedLines = Array.from(lineGroups.entries()).sort((a, b) => a[0] - b[0]);
  const lastLineIdx = sortedLines.length - 1;

  return sortedLines
    .map(([, lineChars], lineIdx) => {
      let s = 0;
      let e = lineChars.length - 1;
      while (s < e && /\s/.test(chars[lineChars[s].index] || '')) s += 1;
      while (e > s && /\s/.test(chars[lineChars[e].index] || '')) e -= 1;

      const first = lineChars[s];
      const last = lineChars[e];
      const lineBoxHeight = Math.max(first.height, last.height);
      const highlightHeight = Math.max(
        MAGIC_HIGHLIGHT_MIN_HEIGHT,
        Math.min(lineBoxHeight, lineBoxHeight * MAGIC_HIGHLIGHT_HEIGHT_RATIO)
      );
      let x = first.x - 4;
      const y = first.y + Math.max(0, (lineBoxHeight - highlightHeight) / 2) + MAGIC_HIGHLIGHT_VERTICAL_NUDGE;
      let rightEdge = last.right + 4;
      const height = highlightHeight;

      if (lineIdx === 0 && frac > 0 && intStart === lineChars[s].index) {
        const nextMetric = metrics[intStart + 1];
        if (nextMetric) {
          if (nextMetric.y === first.y) {
            x += frac * (nextMetric.x - first.x);
          } else {
            x += frac * first.width;
          }
        } else {
          x += frac * (first.width + 8);
        }
      }

      if (lineIdx === lastLineIdx && fracEnd > 0 && intEnd === lineChars[e].index) {
        const beyondMetric = metrics[intEnd + 1];
        if (beyondMetric) {
          if (beyondMetric.y === last.y) {
            rightEdge += fracEnd * (beyondMetric.x - last.right);
          } else {
            rightEdge += fracEnd * (last.width * 0.1);
          }
        }
      }

      const width = rightEdge - x;

      return {
        x,
        y,
        width: Math.max(0, width),
        height,
      };
    })
    .filter((seg) => seg.width > 0);
};

/**
 * Kütüphaneden metin seçtiren sade diyalog. Durağan Metin ekranındaki diyalogdan farkı,
 * yükleme akışı içermemesi: hız egzersizleri yalnızca hazır
 * metinle çalışıyor. Seçilen metin `speed-reading/content/library/{id}` üzerinden çekilir.
 */
const TextLibraryPickerDialog: React.FC<{
  open: boolean;
  mode: ModeConfig;
  onClose: () => void;
  /** true dönerse diyalog kapanır; false dönerse seçim reddedilmiştir, açık kalır. */
  onSelect: (content: RecommendedStoryContent) => boolean | void;
}> = ({ open, mode, onClose, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [items, setItems] = useState<LibraryContentItem[]>([]);
  const [levelGroups, setLevelGroups] = useState<LibraryLevelGroup[]>([]);
  const [search, setSearch] = useState('');
  const [textType, setTextType] = useState('all');

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams({ limit: '200' });
      if (search.trim()) query.set('search', search.trim());
      if (textType !== 'all') query.set('textType', textType);
      const response = await ApiService.getApi(`speed-reading/content/library?${query.toString()}`);
      const nextGroups: LibraryLevelGroup[] = Array.isArray(response?.levels) ? response.levels : [];
      const nextItems: LibraryContentItem[] = Array.isArray(response?.items) ? response.items : [];
      setLevelGroups(nextGroups);
      setItems(nextItems);
    } catch (error) {
      console.error('Metin kütüphanesi yüklenemedi:', error);
      setItems([]);
      setLevelGroups([]);
    } finally {
      setLoading(false);
    }
  }, [search, textType]);

  useEffect(() => {
    if (open) void fetchItems();
  }, [open, fetchItems]);

  const handlePick = useCallback(async (contentId: string) => {
    try {
      setSelecting(true);
      const query = new URLSearchParams({ includeAnswers: 'true', mode: getModeQueryValue(mode) });
      const response = await ApiService.getApi(`speed-reading/content/library/${contentId}?${query.toString()}`);
      const normalized = shared.normalizeRecommendedStoryContent(response);
      if (normalized && onSelect(normalized) !== false) onClose();
    } catch (error) {
      console.error('Seçilen metin yüklenemedi:', error);
    } finally {
      setSelecting(false);
    }
  }, [mode, onSelect, onClose]);

  if (!open) return null;

  const renderCard = (item: LibraryContentItem) => (
    <button
      key={item.id}
      onClick={() => { void handlePick(item.id); }}
      disabled={selecting}
      className="rounded-2xl border border-[#e3ebef] bg-white p-4 text-left transition-colors hover:border-[#9dc2d1] disabled:opacity-60"
    >
      <div className="text-[15px] font-black text-[#22505f]">{item.title}</div>
      <div className="mt-2 flex flex-wrap gap-2 text-[11px] font-semibold text-[#6c8591]">
        {item.category?.name && <span className="rounded-full bg-[#f2f7f9] px-2.5 py-1">{item.category.name}</span>}
        {item.textType && <span className="rounded-full bg-[#f2f7f9] px-2.5 py-1">{item.textType}</span>}
        <span className="rounded-full bg-[#f2f7f9] px-2.5 py-1">{item.wordCount} kelime</span>
      </div>
      {item.summary && <p className="mt-2 line-clamp-2 text-[12px] leading-5 text-[#6b8390]">{item.summary}</p>}
    </button>
  );

  const groupsWithItems = levelGroups.filter((group) => (group.items || []).length > 0);

  return (
    <div className="fixed inset-0 z-[125] flex items-center justify-center bg-black/55 p-4 backdrop-blur-[4px]">
      <div className="w-full max-w-[980px] rounded-2xl bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-[#e9eff2] px-6 py-4">
          <div>
            <h4 className="text-xl font-bold text-[#234d5d]">Metin Kütüphanesi</h4>
            <p className="mt-1 text-sm text-[#738891]">Bu egzersizde çalışmak istediğiniz metni seçin.</p>
          </div>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-[#eef3f5] px-6 py-4">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Başlık, kategori veya içerikte ara"
              className="h-11 rounded-xl border border-[#dce6eb] px-4 text-sm outline-none focus:border-[#8fb8c8]"
            />
            <div className="flex flex-wrap gap-2">
              {[{ value: 'all', label: 'Tümü' }, { value: 'öykü', label: 'Öykü' }, { value: 'makale', label: 'Makale' }].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTextType(option.value)}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold transition-colors"
                  style={getThemedOptionButtonStyle(textType === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => { void fetchItems(); }}
              className="h-11 rounded-xl border border-[#dce6eb] px-4 text-sm font-semibold text-[#335a6a] hover:bg-[#f7fbfd]"
            >
              Yenile
            </button>
          </div>
        </div>

        <div className="max-h-[68vh] overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-4">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#2f5563] border-t-transparent" />
              <p className="text-sm font-semibold text-[#5a7380]">Kütüphane yükleniyor...</p>
            </div>
          ) : items.length === 0 && groupsWithItems.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-4 text-center">
              <Library className="h-10 w-10 text-[#a5b4bb]" />
              <p className="text-sm font-semibold text-[#5a7380]">Bu filtrelerle eşleşen metin bulunamadı.</p>
            </div>
          ) : groupsWithItems.length > 0 ? (
            <div className="space-y-7">
              {groupsWithItems.map((group) => (
                <section key={group.key} className="space-y-3">
                  <h5 className="text-[17px] font-black text-[#1e4c5d]">{group.label}</h5>
                  <div className="grid gap-4 md:grid-cols-2">{(group.items || []).map(renderCard)}</div>
                </section>
              ))}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">{items.map(renderCard)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Metin egzersizlerinin başlığında duran ORTAK metin kaynağı kontrolleri: "Metin Seç" ve
 * Durağan Metin ekranındaki düzenin aynısı; her egzersizde aynı yerde, aynı
 * görünümde olsun diye tek bileşene alındı. Diyalogları da kendi içinde taşır, kullanan
 * ekranın tek yapması gereken onSelect ile gelen metni kendi state'ine yazmak.
 */
export const ExerciseTextSourceControls: React.FC<{
  mode: ModeConfig;
  disabled?: boolean;
  onSelect: (content: RecommendedStoryContent) => void;
  onError?: (message: string) => void;
}> = ({ mode, disabled = false, onSelect, onError }) => {
  const [libraryOpen, setLibraryOpen] = useState(false);

  /**
   * normalizeRecommendedStoryContent, displayMode 'stored-file' ise BOŞ içeriği geçerli sayar
   * (speedReadingShared.tsx: allowsEmptyContent). Yani `!normalized` kontrolü PDF'i yakalamaz;
   * metinsiz kayıt ekrana ulaşır ve egzersiz sessizce örnek metne düşer. Seçim bu yüzden tek noktada doğrulanıyor.
   */
  const acceptContent = useCallback((normalized: RecommendedStoryContent | null) => {
    if (!normalized) {
      onError?.('Seçilen kayıttan okunabilir metin çıkarılamadı.');
      return false;
    }
    if (!String(normalized.content || '').trim()) {
      onError?.('Seçilen kayıtta okunabilir metin yok.');
      return false;
    }
    onSelect(normalized);
    return true;
  }, [onSelect, onError]);

  const buttonClass = 'inline-flex items-center gap-1.5 rounded-full border border-[#d9e3e8] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#55707d] hover:text-[#2f5563] disabled:opacity-50';

  return (
    <>
      <button
        type="button"
        onClick={() => setLibraryOpen(true)}
        disabled={disabled}
        className={buttonClass}
        title="Kütüphaneden metin seç"
      >
        <Library className="w-3.5 h-3.5" />
        Metin Seç
      </button>

      <TextLibraryPickerDialog
        open={libraryOpen}
        mode={mode}
        onClose={() => setLibraryOpen(false)}
        onSelect={acceptContent}
      />
    </>
  );
};

export const GroupingExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  recommendedContent?: RecommendedStoryContent | null;
  contentLoading?: boolean;
  onClose: () => void;
}> = ({ profile, mode, recommendedContent, contentLoading = false, onClose }) => {
  const { user } = useAuth();
  const reportResult = useReportSpeedReadingResult();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<RecommendedStoryContent | null>(null);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'running' | 'paused' | 'review'>('idle');
  const [pausedSource, setPausedSource] = useState<'countdown' | 'running' | null>(null);
  const [countdown, setCountdown] = useState<number>(3);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [runningElapsedMs, setRunningElapsedMs] = useState<number>(0);
  const [openedCount, setOpenedCount] = useState<number>(0);
  const [countdownAction, setCountdownAction] = useState<'start' | 'resume'>('start');
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, 'A' | 'B' | 'C' | 'D' | 'E' | undefined>>({});
  const [activeReviewQuestionIndex, setActiveReviewQuestionIndex] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [showCompletionSummary, setShowCompletionSummary] = useState(false);
  const magicTextLayerRef = useRef<HTMLDivElement | null>(null);
  const magicTextScrollRef = useRef<HTMLDivElement | null>(null);
  const pacedTextScrollRef = useRef<HTMLDivElement | null>(null);
  const pacedGroupRefs = useRef<Record<number, HTMLSpanElement | null>>({});
  const magicCharRefs = useRef<Record<number, HTMLSpanElement | null>>({});
  const magicCharMetricsRef = useRef<Record<number, {
    x: number;
    y: number;
    width: number;
    height: number;
    right: number;
  }>>({});
  const magicOverlayRafRef = useRef<number | null>(null);
  const magicRunningRafRef = useRef<number | null>(null);
  const magicStepLastTsRef = useRef<number | null>(null);
  const magicOverlayElRefs = useRef<(HTMLSpanElement | null)[]>([null, null, null]);
  const magicProgressRef = useRef<number>(0);
  const magicElapsedRef = useRef<number>(0);
  const magicReactSyncTsRef = useRef<number>(0);
  const updateMagicOverlayStaticRef = useRef<() => void>(() => { });
  const modeKey = getModeCanonicalKey(mode);
  const isShadowMode = modeKey === 'golgeleme';
  const isBlockMode = modeKey === 'bloklama';
  const isMagicSpeedMode = modeKey === 'sihirli-hiz';
  const isSmoothTextHighlightMode = isMagicSpeedMode || isShadowMode;
  const isUnifiedPacedTextMode = isShadowMode || isMagicSpeedMode;
  const [settings, setSettings] = useState<GroupingSettings>({
    wordsPerGroup: 2,
    characterCount: MAGIC_CHARACTER_FALLBACK,
    wordsPerMinute: profile?.currentSpeed || 200,
    difficultyLevel: normalizeDifficultyLevel(profile?.readingLevel),
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    theme: 'light',
    accentColor: 'default',
    enableCountdown: true,
  });

  // Kullanıcı kütüphaneden metin seçebilir; seçmediyse öneri metni kullanılır.
  const activeGroupingContent = selectedContent || recommendedContent;

  // Duraklatılmış oturumdan baştan başlamak için tek çıkış yolu. Bu olmadan öğrenci
  // "Devam Et" ve "Sorulara Geç" arasında sıkışıp kalıyor, egzersizi yeniden alamıyordu.
  const handleRestartExercise = useCallback(() => {
    setPhase('idle');
    setPausedSource(null);
    setSessionId(null);
    setRunningElapsedMs(0);
    setOpenedCount(0);
    setSelectedAnswers({});
    setReviewSubmitted(false);
    setShowCompletionSummary(false);
  }, []);

  // Metin değişince çalışma baştan başlamalı; yarım kalan oturum yeni metne taşınmamalı.
  const handleSelectExerciseText = useCallback((content: RecommendedStoryContent) => {
    setSelectedContent(content);
    setPhase('idle');
    setPausedSource(null);
    setSessionId(null);
    setRunningElapsedMs(0);
    setOpenedCount(0);
    setSelectedAnswers({});
    setReviewSubmitted(false);
    setShowCompletionSummary(false);
  }, []);
  const groupingText = (activeGroupingContent?.content || GROUPING_SAMPLE_TEXT).trim();
  const normalizedGroupingText = groupingText.replace(/\s+/g, ' ').trim();
  const words = normalizedGroupingText ? normalizedGroupingText.split(' ') : [];
  const effectiveWordsPerGroup = Math.max(1, settings.wordsPerGroup);
  const groupedWords: string[] = (() => {
    const chunks: string[] = [];
    for (let i = 0; i < words.length; i += effectiveWordsPerGroup) {
      chunks.push(words.slice(i, i + effectiveWordsPerGroup).join(' '));
    }
    return chunks;
  })();
  const magicWindowSize = normalizeCharacterCount(settings.characterCount);
  const magicChars = useMemo(() => Array.from(normalizedGroupingText), [normalizedGroupingText]);
  const shadowGroupCharRanges = useMemo(() => {
    const ranges: Array<{ start: number; length: number }> = [];
    let charIndex = 0;

    for (let groupStart = 0; groupStart < words.length; groupStart += effectiveWordsPerGroup) {
      const groupWords = words.slice(groupStart, groupStart + effectiveWordsPerGroup);
      const text = groupWords.join(' ');
      ranges.push({
        start: charIndex,
        length: Math.max(1, Array.from(text).length),
      });
      charIndex += Array.from(text).length;
      if (groupStart + effectiveWordsPerGroup < words.length) charIndex += 1;
    }

    return ranges;
  }, [effectiveWordsPerGroup, words]);
  const shadowGlyphBaseStyle = useMemo(() => (isShadowMode
    ? {
      color: '#111111',
      filter: 'blur(6px)',
      opacity: 0.42,
    }
    : {
      color: '#2f596b',
    }), [isShadowMode]);
  const magicWordGlyphs = useMemo(() => {
    let charIndex = 0;
    return words.map((word, wordIndex) => {
      const chars = Array.from(word).map((value) => {
        const next = { index: charIndex, value };
        charIndex += 1;
        return next;
      });
      const spaceIndex = wordIndex < words.length - 1 ? charIndex : null;
      if (spaceIndex !== null) charIndex += 1;
      return {
        wordIndex,
        chars,
        spaceIndex,
      };
    });
  }, [words]);
  const magicGlyphNodes = useMemo(() => (
    magicWordGlyphs.map((word) => (
      <React.Fragment key={`word-${word.wordIndex}`}>
        {word.chars.map((char) => (
          <span
            key={`char-${char.index}`}
            ref={(el) => {
              magicCharRefs.current[char.index] = el;
            }}
            className="inline-block px-[1px] py-[1px] whitespace-pre"
            style={shadowGlyphBaseStyle}
          >
            {char.value}
          </span>
        ))}
        {word.spaceIndex !== null && (
          <span
            key={`space-${word.spaceIndex}`}
            ref={(el) => {
              magicCharRefs.current[word.spaceIndex as number] = el;
            }}
            className="inline-block px-[1px] py-[1px] whitespace-pre"
            style={shadowGlyphBaseStyle}
          >
            {' '}
          </span>
        )}
      </React.Fragment>
    ))
  ), [magicWordGlyphs, shadowGlyphBaseStyle]);

  const textWordCount = words.length;
  const totalGroupCount = groupedWords.length;
  const timingWordsPerGroup = effectiveWordsPerGroup;
  const timingMetrics = getGroupingTimingMetrics(
    textWordCount,
    timingWordsPerGroup,
    settings.wordsPerMinute,
    isUnifiedPacedTextMode ? '7 ve 8. Sınıflar' : settings.difficultyLevel,
    groupedWords[groupedWords.length - 1] || ''
  );

  const totalDurationMs = timingMetrics.totalDurationMs;
  const dynamicEstimatedSeconds = timingMetrics.estimatedDurationSeconds;
  const millisecondsPerGroup = timingMetrics.millisecondsPerGroup;

  const themeClass =
    settings.theme === 'dark' ? 'bg-[#132f39] text-[#d8edf4]'
      : settings.theme === 'sepia' ? 'bg-[#f5ecdd] text-[#445964]'
        : 'bg-white text-[#18485c]';

  const fontSizeClass =
    settings.fontSize === 'xkucuk' ? 'text-[11px]'
      : settings.fontSize === 'kucuk' ? 'text-sm'
        : settings.fontSize === 'orta' ? 'text-[18px]'
          : settings.fontSize === 'buyuk' ? 'text-[21px]'
            : 'text-[24px]';
  const fullscreenFontSizeClass =
    settings.fontSize === 'xkucuk' ? 'text-[16px]'
      : settings.fontSize === 'kucuk' ? 'text-[22px]'
        : settings.fontSize === 'orta' ? 'text-[28px]'
          : settings.fontSize === 'buyuk' ? 'text-[34px]'
            : 'text-[40px]';

  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const hiddenGroupColor = activeAccent;
  const elapsedLabel = formatClock(Math.max(0, Math.floor(runningElapsedMs / 1000)));
  const shouldRevealGroups = phase === 'running' || (phase === 'paused' && pausedSource === 'running');
  const effectiveOpenedCount = openedCount;
  const visibleGroupCount = shouldRevealGroups ? effectiveOpenedCount : 0;
  const activeGroupIndex = shouldRevealGroups
    ? Math.min(groupedWords.length - 1, Math.max(0, effectiveOpenedCount - 1))
    : -1;
  const activeShadowGroupRange = activeGroupIndex >= 0 ? shadowGroupCharRanges[activeGroupIndex] : null;
  const activeMagicStartIndex = isShadowMode
    ? activeShadowGroupRange?.start ?? -1
    : activeGroupIndex;
  const activeMagicWindowSize = isShadowMode
    ? activeShadowGroupRange?.length ?? magicWindowSize
    : magicWindowSize;
  const progressGroupCount =
    phase === 'paused' || (phase === 'countdown' && countdownAction === 'resume') || pausedSource === 'running'
      ? effectiveOpenedCount
      : visibleGroupCount;
  const progressPercent = totalGroupCount > 0 ? Math.min(100, (progressGroupCount / totalGroupCount) * 100) : 0;
  // Metnin KENDİ soruları sorulur (tamamı). GROUPING_REVIEW_QUESTIONS tekniği anlatan genel
  // bir settir; yalnızca metin sorusuz geldiğinde yedek olarak kullanılır.
  const reviewQuestions = activeGroupingContent?.questions?.length
    ? activeGroupingContent.questions
    : GROUPING_REVIEW_QUESTIONS;
  const reviewCorrectCount = reviewQuestions.filter((question) => selectedAnswers[question.id] === question.correctKey).length;
  const reviewRatio = reviewQuestions.length > 0 ? reviewCorrectCount / reviewQuestions.length : 0;
  const hasAnsweredAllReviewQuestions = reviewQuestions.every((question) => Boolean(selectedAnswers[question.id]));
  const estimatedReadWords = Math.min(textWordCount, effectiveOpenedCount * effectiveWordsPerGroup);
  const measuredWpm = Math.max(0, Math.round((estimatedReadWords / Math.max(runningElapsedMs / 1000, 1)) * 60));
  const recommendedWpm = reviewRatio >= 0.8
    ? settings.wordsPerMinute + 15
    : reviewRatio >= 0.5
      ? settings.wordsPerMinute
      : Math.max(50, settings.wordsPerMinute - 15);
  const reviewSummary = reviewRatio >= 0.8
    ? 'Metni oldukça iyi anlamış görünüyorsunuz.'
    : reviewRatio >= 0.5
      ? 'Metni genel olarak anlamışsınız, bu hız korunabilir.'
      : 'Anlama biraz zorlanmış görünüyor, hızı biraz düşürmek daha sağlıklı olabilir.';

  useEffect(() => {
    if (phase === 'review') setActiveReviewQuestionIndex(0);
  }, [phase, reviewQuestions.length]);

  const primaryButtonLabel = phase === 'review'
    ? (reviewSubmitted ? 'Modlara Dön' : 'Egzersizi Tamamla')
    : phase === 'running' || phase === 'countdown'
      ? 'Durdur'
      : phase === 'paused'
        ? 'Devam Et'
        : 'Başlat';
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const exerciseTitle = mode.name;
  const isFullscreenReadingPhase = isFullscreen && (phase === 'countdown' || phase === 'running' || phase === 'paused');
  const introParagraphs = isMagicSpeedMode
    ? MAGIC_SPEED_INFO_PARAGRAPHS
    : isShadowMode
      ? SHADOW_INFO_PARAGRAPHS
      : isBlockMode
        ? BLOCKING_INFO_PARAGRAPHS
        : GROUPING_INFO_PARAGRAPHS;
  const activeChunkWords = activeGroupIndex >= 0 ? groupedWords[activeGroupIndex]?.split(' ').filter(Boolean) || [] : [];
  const applyGroupingSharedSettings = (baseSettings: GroupingSettings): GroupingSettings => {
    const applied = applySharedExerciseSettings(baseSettings);
    return {
      ...applied,
      characterCount: normalizeCharacterCount(applied.characterCount),
    };
  };

  const rebuildMagicCharMetrics = useCallback(() => {
    if (!isSmoothTextHighlightMode) return;

    const nextMetrics: Record<number, {
      x: number;
      y: number;
      width: number;
      height: number;
      right: number;
    }> = {};

    Object.entries(magicCharRefs.current).forEach(([rawIndex, el]) => {
      if (!el) return;
      const index = Number(rawIndex);
      const x = el.offsetLeft;
      const y = el.offsetTop;
      const width = el.offsetWidth;
      const height = el.offsetHeight;
      nextMetrics[index] = {
        x,
        y,
        width,
        height,
        right: x + width,
      };
    });

    magicCharMetricsRef.current = nextMetrics;
  }, [isSmoothTextHighlightMode]);

  const applyMagicOverlayToDOM = useCallback((segments: Array<{ x: number; y: number; width: number; height: number }>) => {
    const els = magicOverlayElRefs.current;
    for (let i = 0; i < els.length; i += 1) {
      const el = els[i];
      if (!el) continue;
      const seg = segments[i];
      if (seg && seg.width > 0) {
        el.style.transform = `translate3d(${seg.x}px, ${seg.y}px, 0)`;
        el.style.width = `${seg.width}px`;
        el.style.height = `${seg.height}px`;
        el.style.opacity = '0.34';
        el.style.display = '';
      } else {
        el.style.opacity = '0';
        el.style.display = 'none';
      }
    }
  }, []);

  const applyShadowGlyphFocusToDOM = useCallback((progress: number | null) => {
    if (!isShadowMode) return;

    const focusStart = progress ?? -1;
    const focusEnd = focusStart + activeMagicWindowSize - 1;

    Object.entries(magicCharRefs.current).forEach(([rawIndex, el]) => {
      if (!el) return;

      const index = Number(rawIndex);
      const leadingEdgeStrength = Math.max(0, Math.min(1, index + 1 - focusStart));
      const trailingEdgeStrength = Math.max(0, Math.min(1, focusEnd + 1 - index));
      const focusStrength = focusStart >= 0 ? Math.min(leadingEdgeStrength, trailingEdgeStrength) : 0;
      const blurAmount = 6 - (focusStrength * 6);

      el.style.filter = `blur(${blurAmount.toFixed(2)}px)`;
      el.style.opacity = String(0.42 + (focusStrength * 0.58));
      el.style.color = '#111111';
    });
  }, [activeMagicWindowSize, isShadowMode]);

  updateMagicOverlayStaticRef.current = () => {
    if (phase === 'running' && !isShadowMode) return;
    if (!isSmoothTextHighlightMode || !shouldRevealGroups || activeMagicStartIndex < 0) {
      applyMagicOverlayToDOM([]);
      applyShadowGlyphFocusToDOM(null);
      return;
    }
    const segments = computeMagicSegments(activeMagicStartIndex, magicChars, activeMagicWindowSize, magicCharMetricsRef.current);
    applyMagicOverlayToDOM(segments);
    applyShadowGlyphFocusToDOM(activeMagicStartIndex);
    const scrollLayer = magicTextScrollRef.current;
    if (scrollLayer) {
      const focusMetric = magicCharMetricsRef.current[activeMagicStartIndex];
      if (focusMetric) {
        const viewTop = scrollLayer.scrollTop;
        const viewBottom = viewTop + scrollLayer.clientHeight;
        const topGap = focusMetric.y - viewTop;
        const bottomGap = viewBottom - (focusMetric.y + focusMetric.height);
        if (topGap < 18 || bottomGap < 18) {
          const targetTop = Math.max(0, focusMetric.y - (scrollLayer.clientHeight * 0.45));
          if (Math.abs(scrollLayer.scrollTop - targetTop) > 2) {
            scrollLayer.scrollTop = targetTop;
          }
        }
      }
    }
  };

  const scheduleMagicOverlayUpdate = useCallback(() => {
    if (magicOverlayRafRef.current !== null) {
      window.cancelAnimationFrame(magicOverlayRafRef.current);
    }
    magicOverlayRafRef.current = window.requestAnimationFrame(() => {
      updateMagicOverlayStaticRef.current();
      magicOverlayRafRef.current = null;
    });
  }, []);

  useLayoutEffect(() => {
    if (!isSmoothTextHighlightMode) return;
    rebuildMagicCharMetrics();
    scheduleMagicOverlayUpdate();
  }, [isSmoothTextHighlightMode, rebuildMagicCharMetrics, scheduleMagicOverlayUpdate, settings.fontSize, settings.fontFamily, normalizedGroupingText]);

  useLayoutEffect(() => {
    if (!isSmoothTextHighlightMode || phase !== 'running') return;
    rebuildMagicCharMetrics();
  }, [isSmoothTextHighlightMode, phase, rebuildMagicCharMetrics]);

  useEffect(() => {
    if (!isSmoothTextHighlightMode) return;

    const handleResize = () => {
      rebuildMagicCharMetrics();
      scheduleMagicOverlayUpdate();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSmoothTextHighlightMode, rebuildMagicCharMetrics, scheduleMagicOverlayUpdate]);

  useEffect(() => {
    if (!isSmoothTextHighlightMode || (phase === 'running' && !isShadowMode)) return;
    scheduleMagicOverlayUpdate();
  }, [activeMagicStartIndex, activeMagicWindowSize, isShadowMode, isSmoothTextHighlightMode, phase, scheduleMagicOverlayUpdate]);

  useEffect(() => {
    if (isShadowMode) return;
    Object.values(magicCharRefs.current).forEach((el) => {
      if (!el) return;
      el.style.filter = '';
      el.style.opacity = '';
    });
  }, [isShadowMode]);

  useEffect(() => {
    return () => {
      if (magicOverlayRafRef.current !== null) {
        window.cancelAnimationFrame(magicOverlayRafRef.current);
      }
      if (magicRunningRafRef.current !== null) {
        window.cancelAnimationFrame(magicRunningRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    magicCharRefs.current = {};
    magicCharMetricsRef.current = {};
  }, [normalizedGroupingText]);

  useEffect(() => {
    setSettings((prev) => applyGroupingSharedSettings(prev));
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const loadSettingsAndSession = async () => {
      try {
        const [savedSettings, activeSession] = await fetchGroupingBootstrap(modeKey);

        const defaultSettings: GroupingSettings = {
          wordsPerGroup: 2,
          characterCount: MAGIC_CHARACTER_FALLBACK,
          wordsPerMinute: profile?.currentSpeed || 200,
          difficultyLevel: normalizeDifficultyLevel(profile?.readingLevel),
          fontSize: 'buyuk',
          fontFamily: 'GoogleSans',
          theme: 'light',
          accentColor: 'default',
          enableCountdown: true,
        };

        const normalizeLoadedSettings = (
          incoming: Partial<GroupingSettings> | null | undefined,
          fallback: GroupingSettings
        ): GroupingSettings => ({
          ...fallback,
          ...(incoming || {}),
          wordsPerMinute: Number(incoming?.wordsPerMinute) || fallback.wordsPerMinute,
          wordsPerGroup: Number(incoming?.wordsPerGroup) || fallback.wordsPerGroup,
          characterCount: normalizeCharacterCount(incoming?.characterCount ?? fallback.characterCount),
          difficultyLevel: normalizeDifficultyLevel(incoming?.difficultyLevel || fallback.difficultyLevel),
          accentColor: normalizeAccentColor(incoming?.accentColor || fallback.accentColor),
          enableCountdown: typeof incoming?.enableCountdown === 'boolean' ? incoming.enableCountdown : fallback.enableCountdown,
        });

        const getPreviewTiming = (normalizedSettings: GroupingSettings) => {
          const previewWordsPerGroup = Math.max(1, normalizedSettings.wordsPerGroup);
          const previewGroups: string[] = (() => {
            const chunks: string[] = [];
            for (let i = 0; i < words.length; i += previewWordsPerGroup) {
              chunks.push(words.slice(i, i + previewWordsPerGroup).join(' '));
            }
            return chunks;
          })();
          const previewTimingWordsPerGroup = previewWordsPerGroup;

          return {
            previewGroups,
            timing: getGroupingTimingMetrics(
              words.length,
              previewTimingWordsPerGroup,
              normalizedSettings.wordsPerMinute,
              isUnifiedPacedTextMode ? '7 ve 8. Sınıflar' : normalizedSettings.difficultyLevel,
              previewGroups[previewGroups.length - 1] || ''
            ),
          };
        };

        let effectiveSettings = defaultSettings;

        if (savedSettings) {
          effectiveSettings = normalizeLoadedSettings(savedSettings, effectiveSettings);
        }

        if (activeSession?.sessionId || activeSession?.id) {
          const activeStatus = activeSession.status;
          if ((activeStatus === 'running' || activeStatus === 'paused') && activeSession.settings) {
            effectiveSettings = normalizeLoadedSettings(activeSession.settings, effectiveSettings);
          }

          const { previewGroups, timing } = getPreviewTiming(effectiveSettings);
          setSettings(applyGroupingSharedSettings(effectiveSettings));

          if (activeStatus === 'running' || activeStatus === 'paused') {
            setSessionId(activeSession.sessionId || activeSession.id);
            setRemainingSeconds(activeSession.remainingSeconds || activeSession.estimatedDurationSeconds || timing.estimatedDurationSeconds || 0);
            if (activeStatus === 'paused') {
              setPausedSource('running');
            }
          } else {
            setSessionId(null);
            setRemainingSeconds(timing.estimatedDurationSeconds);
            setRunningElapsedMs(0);
            setOpenedCount(0);
          }
          if ((activeStatus === 'running' || activeStatus === 'paused')
            && Number.isFinite(activeSession.estimatedDurationSeconds)
            && Number.isFinite(activeSession.remainingSeconds)) {
            const elapsedSeconds = Math.max(0, (activeSession.estimatedDurationSeconds || 0) - (activeSession.remainingSeconds || 0));
            const elapsedMs = elapsedSeconds * 1000;
            setRunningElapsedMs(elapsedMs);
            setOpenedCount(Math.min(previewGroups.length, Math.max(1, Math.floor(elapsedMs / timing.millisecondsPerGroup) + 1)));
          }
          if (activeStatus === 'running') setPhase('running');
          if (activeStatus === 'paused') setPhase('paused');
          if (activeStatus === 'ready') setPhase('idle');
        } else {
          const { timing } = getPreviewTiming(effectiveSettings);
          setSettings(applyGroupingSharedSettings(effectiveSettings));
          setRemainingSeconds(timing.estimatedDurationSeconds);
          setRunningElapsedMs(0);
          setOpenedCount(0);
        }
      } catch (error) {
        console.error('Gruplama ayarları yüklenemedi:', error);
      }
    };

    loadSettingsAndSession();
  }, []);

  useEffect(() => {
    if (phase !== 'idle') return;
    setRemainingSeconds(dynamicEstimatedSeconds);
    setRunningElapsedMs(0);
    setOpenedCount(0);
    setReviewSubmitted(false);
    setSelectedAnswers({});
  }, [dynamicEstimatedSeconds, phase]);

  useEffect(() => {
    if (phase !== 'countdown') return;
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0 || !sessionId) return;

    const run = async () => {
      try {
        const nextAction = pausedSource === 'running' || countdownAction === 'resume' ? 'resume' : 'start';

        if (nextAction === 'resume') {
          const resumed = await ApiService.postApi('speed-reading/exercises/grouping/session/action', {
            action: 'resume',
            sessionId,
          });
          setRemainingSeconds((prev) => resumed.remainingSeconds ?? prev);
          setPausedSource(null);
          setPhase('running');
          return;
        }

        const result = await ApiService.postApi('speed-reading/exercises/grouping/session/action', {
          action: 'start',
          sessionId,
        });
        setRemainingSeconds((prev) => result.remainingSeconds ?? prev);
        setRunningElapsedMs(0);
        setOpenedCount(Math.min(totalGroupCount, 1));
        setPausedSource(null);
        setPhase('running');
      } catch (error) {
        console.error('Gruplama başlatılamadı:', error);
        setPhase('idle');
      }
    };

    run();
  }, [phase, countdown, sessionId, countdownAction, pausedSource, totalGroupCount]);

  useEffect(() => {
    if (phase !== 'running') return;
    const interval = window.setInterval(() => {
      setRunningElapsedMs((prev) => Math.min(totalDurationMs, prev + 100));
    }, 100);
    return () => clearInterval(interval);
  }, [phase, totalDurationMs]);

  useEffect(() => {
    if (phase !== 'running' && phase !== 'paused') return;
    const remainingMs = Math.max(0, totalDurationMs - runningElapsedMs);
    setRemainingSeconds(Math.max(0, Math.ceil(remainingMs / 1000)));
  }, [phase, runningElapsedMs, totalDurationMs]);

  useEffect(() => {
    if (phase !== 'running') return;
    if (openedCount >= totalGroupCount) return;

    const timer = window.setTimeout(() => {
      setOpenedCount((current) => (current < totalGroupCount ? current + 1 : current));
    }, millisecondsPerGroup);

    return () => window.clearTimeout(timer);
  }, [phase, openedCount, millisecondsPerGroup, totalGroupCount]);

  useEffect(() => {
    if (!isSmoothTextHighlightMode || isShadowMode || phase !== 'running') {
      if (magicRunningRafRef.current !== null) {
        window.cancelAnimationFrame(magicRunningRafRef.current);
        magicRunningRafRef.current = null;
      }
      magicStepLastTsRef.current = null;
      return;
    }

    rebuildMagicCharMetrics();

    const maxStart = Math.max(0, magicChars.length - magicWindowSize);
    const initialElapsed = Math.min(totalDurationMs, Math.max(0, runningElapsedMs));
    const startedAt = performance.now() - initialElapsed;

    const tick = (now: number) => {
      const elapsedMs = Math.min(totalDurationMs, Math.max(0, now - startedAt));
      const ratio = totalDurationMs > 0 ? Math.min(1, elapsedMs / totalDurationMs) : 1;
      const nextProgress = maxStart * ratio;

      magicProgressRef.current = nextProgress;
      magicElapsedRef.current = elapsedMs;
      applyMagicOverlayToDOM(computeMagicSegments(nextProgress, magicChars, magicWindowSize, magicCharMetricsRef.current));
      applyShadowGlyphFocusToDOM(nextProgress);

      const scrollLayer = magicTextScrollRef.current;
      const focusMetric = magicCharMetricsRef.current[Math.max(0, Math.floor(nextProgress))];
      if (scrollLayer && focusMetric) {
        const targetTop = Math.max(0, focusMetric.y - (scrollLayer.clientHeight * 0.42));
        if (Math.abs(scrollLayer.scrollTop - targetTop) > 3) {
          scrollLayer.scrollTop += (targetTop - scrollLayer.scrollTop) * 0.18;
        }
      }

      if (!magicStepLastTsRef.current || now - magicStepLastTsRef.current > 120 || ratio >= 1) {
        magicStepLastTsRef.current = now;
        setOpenedCount(Math.min(totalGroupCount, Math.max(1, Math.ceil(totalGroupCount * ratio))));
      }

      if (ratio < 1) {
        magicRunningRafRef.current = window.requestAnimationFrame(tick);
      }
    };

    magicRunningRafRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (magicRunningRafRef.current !== null) {
        window.cancelAnimationFrame(magicRunningRafRef.current);
        magicRunningRafRef.current = null;
      }
    };
  }, [
    applyMagicOverlayToDOM,
    applyShadowGlyphFocusToDOM,
    isSmoothTextHighlightMode,
    isShadowMode,
    magicChars,
    magicWindowSize,
    phase,
    rebuildMagicCharMetrics,
    totalDurationMs,
    totalGroupCount,
  ]);

  useEffect(() => {
    if (!isUnifiedPacedTextMode || isShadowMode || phase !== 'running') return;
    const container = pacedTextScrollRef.current;
    const activeNode = pacedGroupRefs.current[activeGroupIndex];
    if (!container || !activeNode) return;

    const targetTop = Math.max(0, activeNode.offsetTop - (container.clientHeight * 0.42));
    if (Math.abs(container.scrollTop - targetTop) > 4) {
      container.scrollTo({ top: targetTop, behavior: 'smooth' });
    }
  }, [activeGroupIndex, isShadowMode, isUnifiedPacedTextMode, phase]);

  useEffect(() => {
    if (phase !== 'running' || runningElapsedMs < totalDurationMs || !sessionId) return;
    ApiService.postApi('speed-reading/exercises/grouping/session/complete', {
      sessionId,
      status: 'completed',
      totalReadWords: textWordCount,
    }).catch((error) => console.error('Gruplama tamamlanamadı:', error));
    setPhase('review');
    setSessionId(null);
    setRemainingSeconds(0);
    setRunningElapsedMs(0);
    setOpenedCount(totalGroupCount);
    setPausedSource(null);
    setCountdown(3);
  }, [phase, runningElapsedMs, totalDurationMs, sessionId, textWordCount, totalGroupCount]);

  const saveSettings = async (nextSettings: GroupingSettings) => {
    setSettings(nextSettings);
    try {
      await ApiService.putApi('speed-reading/exercises/grouping/settings', toGroupingApiSettings(nextSettings));
    } catch (error) {
      console.error('Gruplama ayarları kaydedilemedi:', error);
    }
  };

  const updateSettings = (partial: Partial<GroupingSettings>) => {
    const nextSettings = {
      ...settings,
      ...partial,
      characterCount: normalizeCharacterCount((partial.characterCount as number | undefined) ?? settings.characterCount),
      accentColor: normalizeAccentColor((partial.accentColor as string | undefined) ?? settings.accentColor),
    };
    void saveSettings(nextSettings);
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };

  const handleCloseExercise = async () => {
    if (actionPending) return;

    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    if (sessionId) {
      try {
        await ApiService.postApi('speed-reading/exercises/grouping/session/action', {
          action: 'stop',
          sessionId,
        });
      } catch (error) {
        console.error('Gruplama oturumu durdurulamadı:', error);
      }

      try {
        const estimatedReadWords = Math.min(textWordCount, openedCount * effectiveWordsPerGroup);
        await ApiService.postApi('speed-reading/exercises/grouping/session/complete', {
          sessionId,
          status: 'stopped',
          totalReadWords: estimatedReadWords,
        });
      } catch (error) {
        console.error('Gruplama oturumu sıfırlanamadı:', error);
      }
    }

    setPhase('idle');
    setPausedSource(null);
    setSessionId(null);
    setRunningElapsedMs(0);
    setOpenedCount(0);
    setCountdown(settings.enableCountdown ? 3 : 0);
    setRemainingSeconds(dynamicEstimatedSeconds);
    setShowCompletionSummary(false);
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;

    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handleStart = async () => {
    if (actionPending) return;
    if (contentLoading) return;
    if (phase === 'paused' && pausedSource === 'countdown') {
      await requestExerciseFullscreen(fullscreenContainerRef.current);
      setCountdownAction('start');
      setCountdown(settings.enableCountdown ? 3 : 0);
      setPhase('countdown');
      return;
    }

    if (phase === 'paused' && sessionId) {
      await requestExerciseFullscreen(fullscreenContainerRef.current);
      setCountdownAction('resume');
      setCountdown(settings.enableCountdown ? 3 : 0);
      setPhase('countdown');
      return;
    }

    try {
      await requestExerciseFullscreen(fullscreenContainerRef.current);
      setActionPending(true);
      const prepared: GroupingSessionPrepareResponse = await ApiService.postApi('speed-reading/exercises/grouping/session/prepare', {
        exerciseMode: modeKey,
        textWordCount,
        textCharacterCount: groupingText.replace(/\s+/g, '').length,
        contentId: activeGroupingContent?.id,
        settings: toGroupingApiSettings(settings),
      });
      const preparedDuration = prepared.estimatedDurationSeconds || dynamicEstimatedSeconds;
      const preparedCountdown = settings.enableCountdown ? (prepared.countdownSeconds ?? 3) : 0;
      setSessionId(prepared.sessionId);
      setRemainingSeconds(preparedDuration);
      setRunningElapsedMs(0);
      setOpenedCount(0);
      setPausedSource(null);
      setCountdownAction('start');
      setCountdown(preparedCountdown);

      if (preparedCountdown > 0) {
        setPhase('countdown');
        return;
      }

      const started = await ApiService.postApi('speed-reading/exercises/grouping/session/action', {
        action: 'start',
        sessionId: prepared.sessionId,
      });
      setRemainingSeconds((prev) => started.remainingSeconds ?? prev);
      setPhase('running');
    } catch (error) {
      console.error('Gruplama hazırlanamadı:', error);
    } finally {
      setActionPending(false);
    }
  };

  const handlePause = async () => {
    if (actionPending) return;
    if (phase === 'countdown') {
      setPausedSource('countdown');
      setPhase('paused');
      return;
    }
    if (!sessionId) return;
    try {
      setActionPending(true);
      const paused = await ApiService.postApi('speed-reading/exercises/grouping/session/action', {
        action: 'pause',
        sessionId,
      });
      setRemainingSeconds((prev) => paused.remainingSeconds ?? prev);
      setPausedSource('running');
      setPhase('paused');
    } catch (error) {
      console.error('Gruplama duraklatılamadı:', error);
    } finally {
      setActionPending(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (actionPending) return;
    if (phase === 'review') {
      if (!reviewSubmitted) {
        if (!hasAnsweredAllReviewQuestions) return;
        reportResult({
          questionCount: reviewQuestions.length,
          correctCount: reviewCorrectCount,
          wpm: runningElapsedMs > 0 ? measuredWpm : undefined,
          wordCount: estimatedReadWords || undefined,
          contentId: activeGroupingContent?.id || null,
        });
        setReviewSubmitted(true);
        setShowCompletionSummary(true);
        return;
      }
      await handleCloseExercise();
      return;
    }
    if (phase === 'running' || phase === 'countdown') {
      await handlePause();
      return;
    }
    await handleStart();
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreenReadingPhase ? 'h-screen p-0' : isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className={`h-full overflow-hidden bg-[#f8fafb] flex gap-5 relative ${isFullscreenReadingPhase ? 'border-0 rounded-none px-4 py-4' : 'border border-[#e6edf1] rounded-xl px-6 py-5'}`}>
          <div className="min-h-0 flex-1 flex flex-col">
            <div className={`flex items-center justify-between ${isFullscreenReadingPhase ? 'mb-2' : 'mb-4'}`}>
              <div className="flex items-center gap-1">
                <ScanText className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{exerciseTitle}</div>
              </div>
              <div className="flex items-center gap-1">
                <ExerciseTextSourceControls
                  mode={mode}
                  disabled={phase === 'running' || phase === 'countdown'}
                  onSelect={handleSelectExerciseText}
                  onError={(message) => console.error(message)}
                />
                <button onClick={() => setMuted((m) => !m)} className={EXERCISE_TOOL_BUTTON_CLASS} title={muted ? 'Sesi Aç' : 'Sesi Kapat'}>
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button onClick={handleOpenSettings} className={EXERCISE_TOOL_BUTTON_CLASS} title="Ayarlar">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => { void handleToggleFullscreen(); }}
                  className={EXERCISE_TOOL_BUTTON_CLASS}
                  title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}
                >
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title="Kapat">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className={`min-h-0 flex-1 ${themeClass} overflow-hidden ${isFullscreenReadingPhase ? 'rounded-none border-0 px-8 py-6' : 'border border-[#e8edf0] rounded-[10px] px-6 py-5'}`}>
              {phase === 'review' && (
                <ReviewQuestionsPager
                  questions={reviewQuestions as ReviewPagerQuestion[]}
                  selectedAnswers={selectedAnswers}
                  setSelectedAnswers={setSelectedAnswers}
                  reviewSubmitted={reviewSubmitted}
                  activeQuestionIndex={activeReviewQuestionIndex}
                  setActiveQuestionIndex={setActiveReviewQuestionIndex}
                  fontFamily={activeFontFamily}
                />
              )}

              {(phase === 'countdown' || phase === 'paused') && (
                <div className="h-full flex flex-col items-center justify-center gap-3">
                  {phase === 'paused'
                    ? <Pause className="w-20 h-20 text-[#2f5563]" strokeWidth={2.4} />
                    : <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>}
                </div>
              )}

              {phase === 'idle' && (
                <div className="h-full flex items-center justify-center">
                  <div className="max-w-[980px] translate-y-12 text-center">
                    {contentLoading ? (
                      <div className="mx-auto max-w-[900px] flex flex-col items-center gap-4">
                        <div className="w-9 h-9 border-2 border-[#2f5563] border-t-transparent rounded-full animate-spin" />
                        <p className="text-[20px] font-bold leading-[1.55] text-[#2e6276]">İçerik yükleniyor...</p>
                      </div>
                    ) : (
                      introParagraphs.map((paragraph, index) => (
                        <p
                          key={paragraph}
                          className={`mx-auto max-w-[900px] text-[20px] font-bold leading-[1.55] text-[#2e6276] ${index > 0 ? 'mt-8' : ''}`}
                        >
                          {paragraph}
                        </p>
                      ))
                    )}
                  </div>
                </div>
              )}

              {!(phase === 'countdown' || phase === 'paused' || phase === 'review' || phase === 'idle') && (
                <div className={`h-full ${isFullscreenReadingPhase ? fullscreenFontSizeClass : fontSizeClass} leading-[2.2] select-none`} style={{ fontFamily: activeFontFamily }}>
                  {isBlockMode ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-center">
                        {activeChunkWords.map((word, index) => (
                          <span
                            key={`${word}-${index}-${activeGroupIndex}`}
                            className={`${isFullscreenReadingPhase ? 'text-[76px]' : 'text-[54px]'} font-bold leading-none text-[#111111]`}
                            style={{ fontFamily: activeFontFamily }}
                          >
                            {word}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : isSmoothTextHighlightMode ? (
                    <div ref={magicTextScrollRef} className="h-full overflow-hidden text-[inherit] leading-[inherit] text-[#111111]">
                      <div ref={magicTextLayerRef} className="relative min-h-full pr-2">
                        {[0, 1, 2].map((segmentIndex) => (
                          <span
                            key={`magic-overlay-${segmentIndex}`}
                            ref={(el) => {
                              magicOverlayElRefs.current[segmentIndex] = el;
                            }}
                            className="pointer-events-none absolute left-0 top-0 z-0 rounded-[5px] opacity-0"
                            style={{ backgroundColor: activeAccent }}
                          />
                        ))}
                        <span className="relative z-10">
                          {magicGlyphNodes}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-start gap-x-0 gap-y-0 content-start">
                      {groupedWords.map((chunk, i) => {
                        const isVisible = i < visibleGroupCount;
                        const chunkWithSpace = i === groupedWords.length - 1 ? chunk : `${chunk} `;
                        return (
                          <span
                            key={`${chunk}-${i}`}
                            className="relative inline-block align-baseline whitespace-nowrap transition-none"
                            style={{ minHeight: '1.15em' }}
                          >
                            <span
                              className="invisible pointer-events-none whitespace-pre"
                              style={{
                                fontFamily: activeFontFamily,
                              }}
                            >
                              {chunkWithSpace}
                            </span>

                            {isVisible ? (
                              <span
                                className="absolute inset-0 whitespace-pre"
                                style={{
                                  color: '#111111',
                                  fontFamily: activeFontFamily,
                                }}
                              >
                                {chunkWithSpace}
                              </span>
                            ) : (
                              <span
                                className="absolute inset-y-0 left-0 right-0 rounded-full"
                                style={{
                                  backgroundColor: hiddenGroupColor,
                                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
                                }}
                              />
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {isFullscreenReadingPhase && (
            <div className="absolute bottom-5 right-5 z-30 flex items-center gap-2 rounded-full border border-[#dbe7ec] bg-white/92 px-2 py-2 shadow-[0_16px_45px_rgba(26,72,92,0.18)] backdrop-blur">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={actionPending}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-full px-5 text-sm font-bold text-white disabled:opacity-60"
                style={getThemeChromeButtonStyle()}
                type="button"
              >
                {(primaryButtonLabel === 'Başlat' || primaryButtonLabel === 'Devam Et') && <Play className="w-4 h-4" />}
                {primaryButtonLabel === 'Durdur' && <Pause className="w-4 h-4" />}
                {primaryButtonLabel}
              </button>
              {reviewQuestions.length > 0 && (phase === 'running' || phase === 'paused') && (
                <button
                  onClick={() => setPhase('review')}
                  className="inline-flex h-10 items-center justify-center rounded-full bg-[#24465a] px-5 text-sm font-bold text-white hover:bg-[#1c394a]"
                  type="button"
                >
                  Sorulara Geç
                </button>
              )}
            </div>
          )}

          <aside className={`${isFullscreenReadingPhase ? 'hidden' : 'hidden lg:flex'} min-h-0 w-[320px] shrink-0 h-full rounded-xl border border-[#e0eaee] bg-white p-5 flex-col justify-between`}>
            {/* Panelin üst bölümü sığmadığında kaydırılabilmeli: küçük ekranlarda (1280x720)
                Aktif Metin kartı ve Değiştir butonu ekranın altında kalıp erişilemez oluyordu.
                Alttaki süre + başlat bloğu sabit kalır. */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img
                    src={userImage}
                    alt={userName}
                    className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]"
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">1. Seviye</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{exerciseTitle}</h3>
              </div>

              {/* Bilgi kartı: metin SEÇİMİ başlıktaki ortak Metin Seç kontrolünde,
                  burada yalnızca hangi metnin aktif olduğu gösterilir. */}
              <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#6c8591]">
                  <Library className="w-3.5 h-3.5 text-[#6c8591]" />
                  <span>Aktif Metin</span>
                </div>
                <div className="mt-2 text-sm font-bold text-[#27596a]">
                  {activeGroupingContent?.title?.trim() || 'Önerilen Metin'}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {activeGroupingContent?.category?.name && (
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#667d88]">
                      {activeGroupingContent.category.name}
                    </span>
                  )}
                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#5b7784]">
                    {words.length} kelime
                  </span>
                </div>
              </div>

              {phase === 'idle' && (
                <SidebarPreparationCard paragraphs={introParagraphs} />
              )}

              {phase === 'review' && (
                <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4">
                  <div className="text-xs font-semibold text-[#6c8591]">Değerlendirme</div>
                  <div className="mt-2 text-sm font-bold text-[#27596a]">
                    {isMagicSpeedMode ? 'Metinle ilgili soruyu cevaplayın.' : 'Metinle ilgili soruları cevaplayın.'}
                  </div>
                  <p className="mt-2 text-[13px] leading-6 text-[#5d7681]">
                    {isMagicSpeedMode
                      ? 'Cevabınıza göre anlama düzeyiniz yorumlanacak ve size uygun bir okuma hızı önerilecek.'
                      : 'Cevaplarınıza göre anlama düzeyiniz yorumlanacak ve size uygun bir okuma hızı önerilecek.'}
                  </p>
                </div>
              )}

              {phase === 'review' && reviewSubmitted && (
                <div className="mt-4 rounded-xl border border-[#dceaf0] bg-[#f7fbfd] p-4">
                  <div className="text-xs font-semibold text-[#6c8591]">Değerlendirme Kaydedildi</div>
                  <p className="mt-2 text-[13px] leading-6 text-[#446470]">
                    Sonuç özeti ekranın ortasında gösterilir. Modlara dönerek yeni çalışmaya geçebilirsiniz.
                  </p>
                </div>
              )}


              {phase !== 'idle' && phase !== 'review' && (
                <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-[#6c8591]">
                    <BarChart3 className="w-3.5 h-3.5 text-[#6c8591]" />
                    <span>İlerleme</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-[#dce6eb] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-[width] duration-300"
                      style={{ width: `${progressPercent}%`, backgroundColor: THEME_PRIMARY_CSS }}
                    />
                  </div>
                  <div className="mt-2 text-sm font-bold text-[#27596a]">
                    {progressGroupCount} / {totalGroupCount} kelime grubu
                  </div>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs text-[#98a8b1]">Geçen Süre</p>
              <p className="text-[44px] leading-none font-black mt-1" style={getThemeChromeTextStyle()}>{elapsedLabel}</p>
              <p className="mt-2 text-xs text-[#7d8f98]">Hedef hız: {settings.wordsPerMinute} kelime/dk</p>
              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={actionPending || (phase === 'review' && !reviewSubmitted && !hasAnsweredAllReviewQuestions) || (contentLoading && phase === 'idle')}
                className={`mt-4 w-full h-12 rounded-lg text-white font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${phase === 'review' && !reviewSubmitted && !hasAnsweredAllReviewQuestions ? 'opacity-60 cursor-not-allowed' : ''
                  } ${contentLoading && phase === 'idle' ? 'opacity-60 cursor-not-allowed' : ''} ${actionPending ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={getThemeChromeButtonStyle()}
              >
                {primaryButtonLabel === 'Başlat' && !(contentLoading && phase === 'idle') && <Play className="w-4 h-4" />}
                {primaryButtonLabel === 'Devam Et' && <Play className="w-4 h-4" />}
                {primaryButtonLabel === 'Durdur' && <Pause className="w-4 h-4" />}
                {contentLoading && phase === 'idle' ? 'İçerik Yükleniyor...' : primaryButtonLabel}
              </button>

              {phase !== 'review' && reviewQuestions.length > 0 && (phase === 'running' || phase === 'paused') && (
                <button
                  onClick={() => setPhase('review')}
                  className="mt-4 w-full h-12 rounded-lg bg-[#24465a] text-white font-bold text-base transition-colors inline-flex items-center justify-center hover:bg-[#1c394a]"
                >
                  Sorulara Geç
                </button>
              )}

              {(phase === 'paused' || phase === 'running') && (
                <button
                  onClick={handleRestartExercise}
                  className="mt-3 w-full h-11 rounded-lg border border-[#dbe7ec] bg-white text-[#3d6070] font-semibold text-sm transition-colors hover:bg-[#f4f9fb]"
                  type="button"
                >
                  Baştan Başla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {showCompletionSummary && (
            <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/45 px-4">
              <div className="w-full max-w-2xl rounded-[2rem] border border-[#dce8ee] bg-white p-8 shadow-[0_30px_90px_rgba(26,72,92,0.18)]">
                <div className="text-center">
                  <div className="text-sm font-semibold uppercase tracking-[0.2em] text-[#7a8e98]">Egzersiz Özeti</div>
                  <h3 className="mt-3 text-[2rem] font-black text-[#18485c]">Metin Değerlendirmesi Tamamlandı</h3>
                  <p className="mt-3 text-[15px] leading-7 text-[#5c7480]">{reviewSummary}</p>
                </div>

                <div className="mt-8 grid gap-4 md:grid-cols-3">
                  <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                    <div className="text-xs font-semibold text-[#7a8e98]">Anlama</div>
                    <div className="mt-2 text-[28px] font-black text-[#18485c]">
                      {reviewCorrectCount} / {reviewQuestions.length}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                    <div className="text-xs font-semibold text-[#7a8e98]">Okuma Hızı</div>
                    <div className="mt-2 text-[28px] font-black text-[#18485c]">{measuredWpm} kelime/dk</div>
                  </div>
                  <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                    <div className="text-xs font-semibold text-[#7a8e98]">Önerilen Hız</div>
                    <div className="mt-2 text-[28px] font-black text-[#18485c]">{recommendedWpm} kelime/dk</div>
                  </div>
                </div>

                <button
                  onClick={() => { void handleCloseExercise(); }}
                  className="mt-8 h-12 w-full rounded-xl text-sm font-semibold text-white"
                  style={getThemeChromeButtonStyle()}
                >
                  Modlara Dön
                </button>
              </div>
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-start justify-center overflow-y-auto p-4">
              <div className="w-[920px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg p-6 select-none my-auto">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={handleCloseSettings}>
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute, isUnifiedPacedTextMode ? 1 : settings.wordsPerGroup)} ms`}
                      onDecrease={() => {
                        const speedWordsPerGroup = isUnifiedPacedTextMode ? 1 : settings.wordsPerGroup;
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute, speedWordsPerGroup);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.max(0, currentIndex - 1)];
                        updateSettings({
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs, speedWordsPerGroup),
                          paceMs: nextPaceMs,
                        });
                      }}
                      onIncrease={() => {
                        const speedWordsPerGroup = isUnifiedPacedTextMode ? 1 : settings.wordsPerGroup;
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute, speedWordsPerGroup);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.min(shared.PACE_MS_OPTIONS.length - 1, currentIndex + 1)];
                        updateSettings({
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs, speedWordsPerGroup),
                          paceMs: nextPaceMs,
                        });
                      }}
                    />

                    {isMagicSpeedMode ? (
                      <SettingsStepperRow
                        label="Kelime Sayısı"
                        value={`${settings.wordsPerGroup} kelime`}
                        onDecrease={() => {
                          const currentIndex = shared.WORD_GROUP_OPTIONS.findIndex((option) => option === settings.wordsPerGroup);
                          const nextWordsPerGroup = shared.WORD_GROUP_OPTIONS[Math.max(0, currentIndex - 1)];
                          updateSettings({
                            wordsPerGroup: nextWordsPerGroup,
                          });
                        }}
                        onIncrease={() => {
                          const currentIndex = shared.WORD_GROUP_OPTIONS.findIndex((option) => option === settings.wordsPerGroup);
                          const nextWordsPerGroup = shared.WORD_GROUP_OPTIONS[Math.min(shared.WORD_GROUP_OPTIONS.length - 1, currentIndex + 1)];
                          updateSettings({
                            wordsPerGroup: nextWordsPerGroup,
                          });
                        }}
                      />
                    ) : (
                      <SettingsStepperRow
                        label="Kelime Sayısı"
                        value={`${settings.wordsPerGroup}`}
                        onDecrease={() => {
                          const currentIndex = shared.WORD_GROUP_OPTIONS.findIndex((option) => option === settings.wordsPerGroup);
                          const nextWordsPerGroup = shared.WORD_GROUP_OPTIONS[Math.max(0, currentIndex - 1)];
                          updateSettings({
                            wordsPerGroup: nextWordsPerGroup,
                          });
                        }}
                        onIncrease={() => {
                          const currentIndex = shared.WORD_GROUP_OPTIONS.findIndex((option) => option === settings.wordsPerGroup);
                          const nextWordsPerGroup = shared.WORD_GROUP_OPTIONS[Math.min(shared.WORD_GROUP_OPTIONS.length - 1, currentIndex + 1)];
                          updateSettings({
                            wordsPerGroup: nextWordsPerGroup,
                          });
                        }}
                      />
                    )}

                    {!isShadowMode && (
                      <SettingsStepperRow
                        label="Metin Zorluğu"
                        value={settings.difficultyLevel}
                        onDecrease={() => {
                          const currentIndex = shared.DIFFICULTY_OPTIONS.findIndex((option) => option === settings.difficultyLevel);
                          const nextDifficulty = shared.DIFFICULTY_OPTIONS[Math.max(0, currentIndex - 1)];
                          updateSettings({ difficultyLevel: nextDifficulty });
                        }}
                        onIncrease={() => {
                          const currentIndex = shared.DIFFICULTY_OPTIONS.findIndex((option) => option === settings.difficultyLevel);
                          const nextDifficulty = shared.DIFFICULTY_OPTIONS[Math.min(shared.DIFFICULTY_OPTIONS.length - 1, currentIndex + 1)];
                          updateSettings({ difficultyLevel: nextDifficulty });
                        }}
                      />
                    )}

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => updateSettings({ enableCountdown: !settings.enableCountdown })}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => updateSettings({ fontSize: sizeOption.value })}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => updateSettings({ fontFamily: fontOption.value })}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => updateSettings({ accentColor: colorOption.value })}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

type BlockReadingCatalogItem = {
  id: string;
  textNo: number;
  displayNo: string;
  title: string;
};

type BlockReadingLibrary = {
  id: string;
  name: string;
  label: string;
  count: number;
  items: BlockReadingCatalogItem[];
};

type BlockReadingText = {
  textNo: number;
  displayNo: string;
  title: string;
  libraryId: string;
  libraryName: string;
  libraryLabel: string;
  content: string;
  paragraphs: string[];
  wordCount: number;
  characterCount: number;
};

const BLOCK_READING_WIDTH_OPTIONS = [40, 50, 60, 70, 80, 90, 100] as const;
// Blok başına kelime sayısı ARTIK ölçülerek tahmin edilmiyor, öğrenci seçiyor.
// Hız da kelime/dk olarak seçiliyor; blok süresi ikisinden hesaplanıyor:
//   ms/blok = kelimeSayısı × 60000 / (kelime/dk)
// Eskiden "Seviye 1-9" sabit ms değerleriydi ve gerçek hız blok genişliğine göre
// değiştiği için aynı seviye farklı ekranlarda farklı hız anlamına geliyordu.
const BLOCK_READING_WORDS_PER_BLOCK_MIN = 1;
const BLOCK_READING_WORDS_PER_BLOCK_MAX = 8;
const BLOCK_READING_SPEED_MIN = 60;
const BLOCK_READING_SPEED_MAX = 1500;
const BLOCK_READING_SPEED_STEP = 20;
const BLOCK_READING_MIN_BLOCK_MS = 40;
const BLOCK_READING_INFO_PARAGRAPHS = [
  'Bu sayfa okuma metinindeki kelimeleri gruplandırarak tek satır üzerinde, blok şeklinde görüntüler.',
  'Blok üzerinde gösterilmiş kelime grubu belirlenen süre tamamlandığında bir sonraki kelime grubunu blok üzerine alır. Gözler ile kelime grubunun orta noktasına odaklanarak kelime grubunun tamamını görmeye çalışın.',
];
const BLOCK_READING_FONT_SIZE_CLASS: Record<GroupingSettings['fontSize'], string> = {
  xkucuk: 'text-[16px]',
  kucuk: 'text-[22px]',
  orta: 'text-[28px]',
  buyuk: 'text-[34px]',
  xbuyuk: 'text-[40px]',
};
const BLOCK_READING_FULLSCREEN_FONT_SIZE_CLASS: Record<GroupingSettings['fontSize'], string> = {
  xkucuk: 'text-[26px]',
  kucuk: 'text-[34px]',
  orta: 'text-[42px]',
  buyuk: 'text-[50px]',
  xbuyuk: 'text-[58px]',
};
const BLOCK_READING_FONT_SIZE_PX: Record<GroupingSettings['fontSize'], number> = {
  xkucuk: 16,
  kucuk: 22,
  orta: 28,
  buyuk: 34,
  xbuyuk: 40,
};
// BLOCK_READING_FULLSCREEN_FONT_SIZE_CLASS ile aynı değerler; sığdırma ölçümü için px karşılıkları.
const BLOCK_READING_FULLSCREEN_FONT_SIZE_PX: Record<GroupingSettings['fontSize'], number> = {
  xkucuk: 26,
  kucuk: 34,
  orta: 42,
  buyuk: 50,
  xbuyuk: 58,
};

let blockReadingMeasureContext: CanvasRenderingContext2D | null = null;

const getBlockReadingMeasureContext = () => {
  if (typeof document === 'undefined') return null;
  if (!blockReadingMeasureContext) {
    blockReadingMeasureContext = document.createElement('canvas').getContext('2d');
  }
  return blockReadingMeasureContext;
};

/**
 * Metni SABİT kelime sayılı bloklara ayırır. Eskiden bloklar piksel genişliğine göre
 * dolduruluyordu; bu yüzden bloklar eşit değildi ve aynı ayar farklı ekran genişliğinde
 * farklı sayıda kelime veriyordu. Artık blok başına kelime sayısı öğrencinin seçtiği
 * değerdir, ekrandan bağımsızdır.
 */
const buildBlockReadingChunks = (text: string, wordsPerBlock: number) => {
  const words = text.replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
  const size = Math.max(1, Math.round(Number(wordsPerBlock) || 1));
  const chunks: string[] = [];

  for (let index = 0; index < words.length; index += size) {
    chunks.push(words.slice(index, index + size).join(' '));
  }

  return chunks;
};

/**
 * Sabit kelime sayısı kutuya sığmayabilir (uzun kelimeler). Blok kutusu overflow-hidden
 * olduğu için kelime kırpılmasın diye metin gerektiği kadar küçültülür.
 */
const getBlockReadingFitScale = (
  chunk: string,
  widthPercent: number,
  fontPx: number,
  fontFamily: string,
  viewportWidth: number
) => {
  if (!chunk || viewportWidth <= 0) return 1;
  const context = getBlockReadingMeasureContext();
  if (!context) return 1;

  const blockWidth = (viewportWidth * widthPercent) / 100;
  const maxTextWidth = Math.max(80, blockWidth - 64);
  context.font = `600 ${fontPx}px ${fontFamily}`;
  const measured = context.measureText(chunk).width;
  if (measured <= maxTextWidth) return 1;

  return Math.max(0.4, maxTextWidth / measured);
};

export const BlockReadingExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  onClose: () => void;
}> = ({ profile, mode, onClose }) => {
  const { user } = useAuth();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const readingAreaRef = useRef<HTMLDivElement | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [textLoading, setTextLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<BlockReadingLibrary[]>([]);
  const [selectedLibraryId, setSelectedLibraryId] = useState('renkli-dunya-1');
  const [selectedTextNo, setSelectedTextNo] = useState(1);
  const [activeText, setActiveText] = useState<BlockReadingText | null>(null);
  // Genişlik artık kelime sayısını belirlemiyor, yalnızca kutunun görsel genişliği.
  // Varsayılan 4 kelimelik blok %40'lık kutuya sığmadığı için %70'e çekildi.
  const [widthPercent, setWidthPercent] = useState<(typeof BLOCK_READING_WIDTH_OPTIONS)[number]>(70);
  const [wordsPerBlock, setWordsPerBlock] = useState(4);
  const [wordsPerMinute, setWordsPerMinute] = useState(480);
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'running' | 'paused' | 'completed'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const blockSharedDefaults = useMemo(
    () => applySharedExerciseSettings({
      fontSize: 'orta',
      fontFamily: 'GoogleSans',
      accentColor: 'default',
      enableCountdown: true,
    }),
    [],
  );
  const [fontSize, setFontSize] = useState<GroupingSettings['fontSize']>((blockSharedDefaults.fontSize as GroupingSettings['fontSize']) || 'orta');
  const [fontFamily, setFontFamily] = useState(String(blockSharedDefaults.fontFamily || 'GoogleSans'));
  const [accentColor, setAccentColor] = useState(normalizeAccentColor(String(blockSharedDefaults.accentColor || 'default')));
  const [enableCountdown, setEnableCountdown] = useState(Boolean(blockSharedDefaults.enableCountdown ?? true));
  const [openSettingsMenu, setOpenSettingsMenu] = useState<'library' | 'text' | null>(null);
  const [readingAreaWidth, setReadingAreaWidth] = useState(0);

  const selectedLibrary = useMemo(
    () => catalog.find((library) => library.id === selectedLibraryId) || catalog[0] || null,
    [catalog, selectedLibraryId]
  );
  const selectedTextItem = selectedLibrary?.items.find((item) => item.textNo === selectedTextNo) || null;
  const blockReadingSettingsDisabled = phase === 'running' || phase === 'countdown' || catalogLoading;
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === fontFamily)?.css || fontFamily;
  const chunks = useMemo(
    () => buildBlockReadingChunks(activeText?.content || '', wordsPerBlock),
    [activeText?.content, wordsPerBlock]
  );
  // Blok süresi seçilen kelime sayısı ile hedef hızdan çıkar; hız artık gerçekten seçilebilir.
  const levelMs = Math.max(
    BLOCK_READING_MIN_BLOCK_MS,
    Math.round((wordsPerBlock * 60000) / Math.max(1, wordsPerMinute))
  );
  const estimatedSeconds = chunks.length > 0 ? Math.max(1, Math.ceil((chunks.length * levelMs) / 1000)) : 0;
  const progressedBlockCount = phase === 'idle'
    ? 0
    : phase === 'completed'
      ? chunks.length
      : Math.min(activeIndex + 1, chunks.length);
  const progressPercent = chunks.length > 0 ? Math.min(100, (progressedBlockCount / chunks.length) * 100) : 0;
  const wordCount = activeText?.wordCount || 0;
  const characterCount = activeText?.characterCount || 0;
  const readingSpeed = estimatedSeconds > 0 ? Math.round((wordCount / estimatedSeconds) * 60) : 0;
  const currentChunk = chunks[Math.min(activeIndex, Math.max(0, chunks.length - 1))] || '';
  const activeAccent = resolveAccentColor(accentColor);
  const isFullscreenReadingPhase = isFullscreen && (phase === 'countdown' || phase === 'running' || phase === 'paused');
  const blockTextSizeClass = isFullscreenReadingPhase
    ? BLOCK_READING_FULLSCREEN_FONT_SIZE_CLASS[fontSize]
    : BLOCK_READING_FONT_SIZE_CLASS[fontSize];
  const activeBlockWidthPercent = isFullscreenReadingPhase ? Math.max(widthPercent, 72) : widthPercent;
  const blockFitScale = getBlockReadingFitScale(
    currentChunk,
    activeBlockWidthPercent,
    isFullscreenReadingPhase ? BLOCK_READING_FULLSCREEN_FONT_SIZE_PX[fontSize] : BLOCK_READING_FONT_SIZE_PX[fontSize],
    activeFontFamily,
    readingAreaWidth
  );
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');

  useEffect(() => {
    persistSharedExerciseSettings({
      fontSize,
      fontFamily,
      accentColor,
      enableCountdown,
    });
  }, [accentColor, enableCountdown, fontFamily, fontSize]);

  useEffect(() => {
    let cancelled = false;

    const loadCatalog = async () => {
      try {
        setCatalogLoading(true);
        setCatalogError(null);
        const response = await ApiService.getApi('speed-reading/exercises/block-reading/catalog');
        const libraries = Array.isArray(response?.libraries) ? response.libraries as BlockReadingLibrary[] : [];
        if (cancelled) return;
        setCatalog(libraries);
        const firstLibrary = libraries[0];
        const firstText = firstLibrary?.items?.[0];
        if (firstLibrary && firstText) {
          setSelectedLibraryId(firstLibrary.id);
          setSelectedTextNo(firstText.textNo);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Blok Okuma katalog verisi alınamadı:', error);
          setCatalogError('Metin listesi yüklenemedi.');
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };

    void loadCatalog();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTextNo) return;
    let cancelled = false;

    const loadText = async () => {
      try {
        setTextLoading(true);
        const response = await ApiService.getApi(`speed-reading/exercises/block-reading/texts/${selectedTextNo}`);
        if (cancelled) return;
        setActiveText(response as BlockReadingText);
        setPhase('idle');
        setActiveIndex(0);
      } catch (error) {
        if (!cancelled) {
          console.error('Blok Okuma metni alınamadı:', error);
          setActiveText(null);
        }
      } finally {
        if (!cancelled) setTextLoading(false);
      }
    };

    void loadText();

    return () => {
      cancelled = true;
    };
  }, [selectedTextNo]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useLayoutEffect(() => {
    const element = readingAreaRef.current;
    if (!element) return;

    const updateReadingAreaWidth = () => {
      setReadingAreaWidth(Math.max(0, element.clientWidth - 48));
    };

    updateReadingAreaWidth();
    const resizeObserver = new ResizeObserver(updateReadingAreaWidth);
    resizeObserver.observe(element);
    window.addEventListener('resize', updateReadingAreaWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateReadingAreaWidth);
    };
  }, [isFullscreen]);

  useEffect(() => {
    if (phase !== 'countdown') return;
    if (countdown <= 0) {
      setPhase('running');
      return;
    }

    const timer = window.setInterval(() => {
      setCountdown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [countdown, phase]);

  useEffect(() => {
    if (phase !== 'running') return;
    if (chunks.length === 0) {
      setPhase('idle');
      return;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => {
        if (current >= chunks.length - 1) {
          window.clearInterval(timer);
          setPhase('completed');
          return current;
        }
        return current + 1;
      });
    }, levelMs);

    return () => window.clearInterval(timer);
  }, [chunks.length, levelMs, phase]);

  const handleLibraryChange = (libraryId: string) => {
    const library = catalog.find((item) => item.id === libraryId);
    const firstText = library?.items?.[0];
    if (!library || !firstText) return;
    handleReset();
    setOpenSettingsMenu(null);
    setSelectedLibraryId(library.id);
    setSelectedTextNo(firstText.textNo);
  };

  const handleTextChange = (textNo: number) => {
    handleReset();
    setOpenSettingsMenu(null);
    setSelectedTextNo(textNo);
  };

  const updateWidthPercent = (nextWidthPercent: number) => {
    if (phase === 'running' || phase === 'countdown') return;
    const normalizedWidth = Math.max(40, Math.min(100, Math.round(nextWidthPercent / 10) * 10));
    if (!BLOCK_READING_WIDTH_OPTIONS.includes(normalizedWidth as (typeof BLOCK_READING_WIDTH_OPTIONS)[number])) return;
    setWidthPercent(normalizedWidth as (typeof BLOCK_READING_WIDTH_OPTIONS)[number]);
    handleReset();
  };

  const updateWordsPerBlock = (nextWordsPerBlock: number) => {
    if (phase === 'running' || phase === 'countdown') return;
    const normalized = Math.max(
      BLOCK_READING_WORDS_PER_BLOCK_MIN,
      Math.min(BLOCK_READING_WORDS_PER_BLOCK_MAX, Math.round(nextWordsPerBlock))
    );
    if (normalized === wordsPerBlock) return;
    setWordsPerBlock(normalized);
    handleReset();
  };

  const updateWordsPerMinute = (nextWordsPerMinute: number) => {
    if (phase === 'running' || phase === 'countdown') return;
    const normalized = Math.max(
      BLOCK_READING_SPEED_MIN,
      Math.min(
        BLOCK_READING_SPEED_MAX,
        Math.round(nextWordsPerMinute / BLOCK_READING_SPEED_STEP) * BLOCK_READING_SPEED_STEP
      )
    );
    if (normalized === wordsPerMinute) return;
    setWordsPerMinute(normalized);
    handleReset();
  };

  const handlePrimaryAction = async () => {
    if (catalogLoading || textLoading || !activeText || chunks.length === 0) return;
    if (phase === 'running') {
      setPhase('paused');
      return;
    }
    if (phase === 'countdown') {
      setPhase('idle');
      setCountdown(3);
      return;
    }
    if (phase === 'paused') {
      setPhase('running');
      return;
    }

    await requestExerciseFullscreen(fullscreenContainerRef.current);
    setActiveIndex(0);
    setCountdown(3);
    if (enableCountdown) {
      setPhase('countdown');
      return;
    }
    setPhase('running');
  };

  const handleReset = () => {
    setActiveIndex(0);
    setCountdown(3);
    setPhase('idle');
  };

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }
      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreenReadingPhase ? 'h-screen p-0' : isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className={`h-full overflow-hidden bg-[#f8fafb] flex gap-5 relative ${isFullscreenReadingPhase ? 'border-0 rounded-none px-4 py-4' : 'border border-[#e6edf1] rounded-xl px-6 py-5'}`}>
          <div className="min-h-0 flex-1 flex flex-col">
            <div className={`flex items-center justify-between ${isFullscreenReadingPhase ? 'mb-2' : 'mb-4'}`}>
              <div className="flex items-center gap-2">
                <ScanText className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setSettingsOpen(true)} className={EXERCISE_TOOL_BUTTON_CLASS} title="Ayarlar">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title="Kapat">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className={`min-h-0 flex-1 overflow-hidden bg-white ${isFullscreenReadingPhase ? 'rounded-none border-0' : 'rounded-xl border border-[#dbe7ec]'}`}>
              <div ref={readingAreaRef} className={`relative flex h-full items-center justify-center bg-white ${isFullscreenReadingPhase ? 'px-10' : 'px-6'}`}>
                {(catalogLoading || textLoading) && (
                  <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 bg-white/85">
                    <div className="w-9 h-9 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: activeAccent, borderTopColor: 'transparent' }} />
                    <p className="text-sm font-semibold text-[#556b76]">Metin hazırlanıyor...</p>
                  </div>
                )}

                {phase === 'idle' && !catalogLoading && !textLoading && (
                  <div className="max-w-[900px] text-center">
                    {BLOCK_READING_INFO_PARAGRAPHS.map((paragraph, index) => (
                      <p key={paragraph} className={`text-[20px] font-bold leading-[1.55] text-[#4e5b63] ${index > 0 ? 'mt-5' : ''}`}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}

                {phase === 'countdown' && (
                  <div className="flex h-full items-center justify-center">
                    <span className="text-7xl font-extrabold" style={{ color: activeAccent }}>{countdown}</span>
                  </div>
                )}

                {(phase === 'running' || phase === 'paused') && (
                  <div
                    className={`flex items-center justify-center overflow-hidden rounded-[6px] text-center font-semibold leading-none text-[#f1f1f1] shadow-[0_12px_30px_rgba(0,0,0,0.12)] ${isFullscreenReadingPhase ? 'h-36 px-10' : 'h-24 px-5'} ${blockTextSizeClass}`}
                    style={{ width: `${activeBlockWidthPercent}%`, backgroundColor: activeAccent, fontFamily: activeFontFamily }}
                  >
                    <span
                      className="block max-w-full whitespace-nowrap"
                      style={{ transform: `scale(${blockFitScale})`, transformOrigin: 'center' }}
                    >
                      {currentChunk}
                    </span>
                  </div>
                )}

                {phase === 'completed' && (
                  <div className="w-full max-w-[620px] rounded-2xl border border-[#dfe9ee] bg-white p-8 text-center shadow-[0_18px_55px_rgba(34,64,78,0.08)]">
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[28px] bg-[#fff4ed]">
                      <ScanText className="h-10 w-10" style={getThemeChromeTextStyle()} />
                    </div>
                    <h4 className="mt-5 text-[30px] font-black leading-tight sm:text-[34px]" style={getThemeChromeTextStyle()}>Çalışma Tamamlandı</h4>
                    <p className="mx-auto mt-3 max-w-[420px] text-[15px] font-bold leading-6 text-[#5d7480]">
                      {activeText?.displayNo} - {activeText?.title}
                    </p>

                    <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {[
                        { label: 'Blok', value: chunks.length },
                        { label: 'Kelime', value: wordCount },
                        { label: 'Süre', value: formatClock(estimatedSeconds) },
                        { label: 'Hız', value: readingSpeed },
                      ].map((item) => (
                        <div key={item.label} className="rounded-xl border border-[#e5eef2] bg-[#f7fafc] px-3 py-4">
                          <div className="text-[12px] font-bold text-[#a2b1b9]">{item.label}</div>
                          <div className="mt-1 text-[20px] font-black leading-none text-[#194e62]">{item.value}</div>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleReset}
                      className="mt-7 h-12 rounded-lg px-8 text-base font-bold text-white"
                      style={getThemeChromeButtonStyle()}
                    >
                      Yeni Çalışma Başlat
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {isFullscreenReadingPhase && (
            <div className="absolute bottom-5 right-5 z-30 flex items-center gap-2 rounded-full border border-[#dbe7ec] bg-white/92 px-2 py-2 shadow-[0_16px_45px_rgba(26,72,92,0.18)] backdrop-blur">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={catalogLoading || textLoading || !activeText}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-full px-5 text-sm font-bold text-white disabled:opacity-60"
                style={getThemeChromeButtonStyle()}
                type="button"
              >
                {phase === 'paused' && <Play className="w-4 h-4" />}
                {(phase === 'running' || phase === 'countdown') && <Pause className="w-4 h-4" />}
                {phase === 'running' || phase === 'countdown' ? 'Durdur' : phase === 'paused' ? 'Devam Et' : 'Başlat'}
              </button>
            </div>
          )}

          <aside className={`${isFullscreenReadingPhase ? 'hidden' : 'hidden lg:flex'} w-[320px] shrink-0 h-full rounded-xl border border-[#e0eaee] bg-white p-5 flex-col justify-between`}>
            <div>
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">{wordsPerBlock} kelime/blok · {wordsPerMinute} kelime/dk</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Kelime</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{wordCount}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Karakter</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{characterCount}</div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Süre</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{formatClock(estimatedSeconds)}</div>
                </div>
                <div>
                  <div className="text-[14px] text-[#b1b8bd]">Hız</div>
                  <div className="mt-1 text-[20px] leading-none font-black text-[#194e62]">{readingSpeed}</div>
                  <div className="text-[11px] text-[#b1b8bd]">kelime/dk</div>
                </div>
              </div>

              <div className="mt-5 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#6c8591]">
                  <Library className="w-3.5 h-3.5 text-[#6c8591]" />
                  <span>Metin</span>
                </div>
                <div className="mt-2 text-sm font-bold text-[#27596a]">{activeText?.displayNo} - {activeText?.title || 'Yükleniyor'}</div>
                <p className="mt-2 text-[13px] leading-6 text-[#5d7681]">{activeText?.libraryLabel || selectedLibrary?.label}</p>
              </div>

              <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#6c8591]">
                  <BarChart3 className="w-3.5 h-3.5 text-[#6c8591]" />
                  <span>İlerleme</span>
                </div>
                <div className="mt-2 h-2 rounded-full bg-[#dce6eb] overflow-hidden">
                  <div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${progressPercent}%`, backgroundColor: activeAccent }} />
                </div>
                <div className="mt-2 text-sm font-bold text-[#27596a]">
                  {progressedBlockCount} / {chunks.length} blok
                </div>
              </div>
            </div>

            <div>
              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={catalogLoading || textLoading || !activeText}
                className={`w-full h-12 rounded-lg text-white font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${(catalogLoading || textLoading || !activeText) ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={getThemeChromeButtonStyle()}
              >
                {(phase === 'idle' || phase === 'completed' || phase === 'paused') && <Play className="w-4 h-4" />}
                {(phase === 'running' || phase === 'countdown') && <Pause className="w-4 h-4" />}
                {phase === 'running' || phase === 'countdown' ? 'Durdur' : phase === 'paused' ? 'Devam Et' : 'Başlat'}
              </button>

              {(phase === 'paused' || phase === 'completed') && (
                <button
                  onClick={handleReset}
                  className="mt-3 w-full h-11 rounded-lg border border-[#d8e3e8] bg-white text-sm font-bold text-[#355c68] hover:bg-[#f7fbfd]"
                >
                  Sıfırla
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => {
                    setOpenSettingsMenu(null);
                    setSettingsOpen(false);
                  }}>
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="flex min-h-[360px] flex-col justify-between">
                    <div className="space-y-5">
                      <div className="relative rounded-xl border border-[#d9e6eb] bg-white">
                        <div className="grid min-h-14 grid-cols-[185px_minmax(0,1fr)] items-center">
                          <div className="border-r border-[#e7eef2] px-4 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">Kütüphane</div>
                          <button
                            type="button"
                            onClick={() => setOpenSettingsMenu((current) => current === 'library' ? null : 'library')}
                            disabled={blockReadingSettingsDisabled}
                            className="flex h-14 min-w-0 items-center justify-between gap-3 px-4 text-left text-sm font-black text-[#5d7380] outline-none disabled:opacity-60"
                          >
                            <span className="truncate">{selectedLibrary?.label || 'Kütüphane seçin'}</span>
                            <ChevronDown className={`h-4 w-4 shrink-0 text-[#8fa0aa] transition-transform ${openSettingsMenu === 'library' ? 'rotate-180' : ''}`} />
                          </button>
                        </div>

                        {openSettingsMenu === 'library' && !blockReadingSettingsDisabled && (
                          <div className="absolute left-[185px] right-0 top-[calc(100%+6px)] z-[160] overflow-hidden rounded-xl border border-[#d9e6eb] bg-white shadow-[0_18px_45px_rgba(31,64,78,0.14)]">
                            {catalog.map((library) => {
                              const isActive = library.id === selectedLibraryId;
                              return (
                                <button
                                  key={library.id}
                                  type="button"
                                  onClick={() => handleLibraryChange(library.id)}
                                  className={`block w-full px-4 py-3 text-left text-sm font-bold transition-colors ${isActive ? '' : 'text-[#536c79] hover:bg-[#f8fbfc]'}`}
                                  style={isActive ? { backgroundColor: `${activeAccent}14`, color: activeAccent } : undefined}
                                >
                                  {library.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className="relative rounded-xl border border-[#d9e6eb] bg-white">
                        <div className="grid min-h-14 grid-cols-[185px_minmax(0,1fr)] items-center">
                          <div className="border-r border-[#e7eef2] px-4 text-sm font-bold uppercase tracking-[0.08em] text-[#8b9ca5]">Metin Başlığı</div>
                          <button
                            type="button"
                            onClick={() => setOpenSettingsMenu((current) => current === 'text' ? null : 'text')}
                            disabled={blockReadingSettingsDisabled}
                            className="flex h-14 min-w-0 items-center justify-between gap-3 px-4 text-left text-sm font-black text-[#5d7380] outline-none disabled:opacity-60"
                          >
                            <span className="truncate">
                              {selectedTextItem ? `${selectedTextItem.displayNo} - ${selectedTextItem.title}` : 'Metin seçin'}
                            </span>
                            <ChevronDown className={`h-4 w-4 shrink-0 text-[#8fa0aa] transition-transform ${openSettingsMenu === 'text' ? 'rotate-180' : ''}`} />
                          </button>
                        </div>

                        {openSettingsMenu === 'text' && !blockReadingSettingsDisabled && (
                          <div className="absolute left-[185px] right-0 top-[calc(100%+6px)] z-[160] max-h-[260px] overflow-y-auto rounded-xl border border-[#d9e6eb] bg-white py-1 shadow-[0_18px_45px_rgba(31,64,78,0.14)]">
                            {(selectedLibrary?.items || []).map((item) => {
                              const isActive = item.textNo === selectedTextNo;
                              return (
                                <button
                                  key={item.textNo}
                                  type="button"
                                  onClick={() => handleTextChange(item.textNo)}
                                  className={`block w-full px-4 py-2.5 text-left text-sm font-bold transition-colors ${isActive ? '' : 'text-[#536c79] hover:bg-[#f8fbfc]'}`}
                                  style={isActive ? { backgroundColor: `${activeAccent}14`, color: activeAccent } : undefined}
                                >
                                  {item.displayNo} - {item.title}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <SettingsStepperRow
                        label="Blok Kelime Sayısı"
                        value={`${wordsPerBlock} kelime`}
                        onDecrease={() => updateWordsPerBlock(wordsPerBlock - 1)}
                        onIncrease={() => updateWordsPerBlock(wordsPerBlock + 1)}
                      />

                      <SettingsStepperRow
                        label="Hız"
                        value={`${wordsPerMinute} kelime/dk`}
                        onDecrease={() => updateWordsPerMinute(wordsPerMinute - BLOCK_READING_SPEED_STEP)}
                        onIncrease={() => updateWordsPerMinute(wordsPerMinute + BLOCK_READING_SPEED_STEP)}
                      />

                      <SettingsStepperRow
                        label="Genişlik"
                        value={`${widthPercent}%`}
                        onDecrease={() => updateWidthPercent(widthPercent - 10)}
                        onIncrease={() => updateWidthPercent(widthPercent + 10)}
                      />

                      <div className="rounded-xl border border-[#edf2f5] bg-[#f8fbfc] px-4 py-3 text-[12px] font-semibold text-[#6d838e]">
                        Her blok {levelMs} ms ekranda kalır · toplam {chunks.length} blok
                      </div>

                      {/* {(phase === 'running' || phase === 'countdown') && (
                        <div className="rounded-xl border border-[#edf2f5] bg-[#f8fbfc] p-4 text-sm font-semibold text-[#6d838e]">
                          Ayarları değiştirmek için çalışmayı duraklatın.
                        </div>
                      )} */}
                    </div>

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={enableCountdown}
                      onChange={() => setEnableCountdown((prev) => !prev)}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setFontSize(sizeOption.value)}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setFontFamily(fontOption.value)}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setAccentColor(colorOption.value)}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TEXT_READING_FULLSCREEN_FONT_SIZE_CLASS: Record<GroupingSettings['fontSize'], string> = {
  xkucuk: 'text-[20px] leading-[1.68]',
  kucuk: 'text-[24px] leading-[1.72]',
  orta: 'text-[28px] leading-[1.72]',
  buyuk: 'text-[32px] leading-[1.76]',
  xbuyuk: 'text-[36px] leading-[1.78]',
};

// Sayfalama paragraf sınırından böler; bu yüzden okunacak metnin gerçekten paragraflara
// ayrılmış olması gerekir. İki koruma birden:
//   1) Paragraf ayracı olarak yalnızca \n\n aranırsa, tek \n ile kaydedilmiş içerik tek dev
//      paragraf olur ve sayfalama bölecek yer bulamaz (metin ekranın altında kalır).
//   2) Hiç satır sonu olmayan uzun bir paragraf da tek başına sayfaya sığmayabilir; böyle
//      paragraflar cümle sınırından parçalanır.
const READING_PARAGRAPH_MAX_CHARS = 500;

const splitReadingParagraphs = (text: string) => {
  const normalized = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!normalized) return [];

  const blocks = (normalized.includes('\n\n') ? normalized.split(/\n{2,}/) : normalized.split(/\n+/))
    .map((block) => block.trim())
    .filter(Boolean);

  return blocks.flatMap((block) => {
    if (block.length <= READING_PARAGRAPH_MAX_CHARS) return [block];

    const sentences = block.match(/[^.!?…]+[.!?…]+["”']?\s*|[^.!?…]+$/g) || [block];
    const chunks: string[] = [];
    let current = '';

    sentences.forEach((sentence) => {
      const next = current ? `${current}${sentence}` : sentence;
      if (current && next.trim().length > READING_PARAGRAPH_MAX_CHARS) {
        chunks.push(current.trim());
        current = sentence;
      } else {
        current = next;
      }
    });
    if (current.trim()) chunks.push(current.trim());

    return chunks;
  });
};

export const TextReadingExerciseView: React.FC<{
  profile: any;
  mode: ModeConfig;
  recommendedContent?: RecommendedStoryContent | null;
  contentLoading?: boolean;
  onClose: () => void;
}> = ({ profile, mode, recommendedContent, contentLoading = false, onClose }) => {
  const { user } = useAuth();
  const reportResult = useReportSpeedReadingResult();
  const fullscreenContainerRef = useRef<HTMLDivElement | null>(null);
  const storedPdfScrollRef = useRef<HTMLDivElement | null>(null);
  const storedPdfPageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const metronomeTimerRef = useRef<number | null>(null);
  const metronomeAudioRef = useRef<HTMLAudioElement | null>(null);
  const lastRecommendedContentIdRef = useRef<string | null>(recommendedContent?.id || null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [selectedContentLoading, setSelectedContentLoading] = useState(false);
  const [libraryItems, setLibraryItems] = useState<LibraryContentItem[]>([]);
  const [libraryLevelGroups, setLibraryLevelGroups] = useState<LibraryLevelGroup[]>([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [libraryTextType, setLibraryTextType] = useState<string>('all');
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'countdown' | 'reading' | 'paused' | 'review'>('idle');
  const [countdown, setCountdown] = useState(3);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, 'A' | 'B' | 'C' | 'D' | 'E' | undefined>>({});
  const [activeReviewQuestionIndex, setActiveReviewQuestionIndex] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [showCompletionSummary, setShowCompletionSummary] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [freeReadingSpeedScore, setFreeReadingSpeedScore] = useState(3);
  const [freeReadingComprehensionScore, setFreeReadingComprehensionScore] = useState(3);
  const [freeReadingNotes, setFreeReadingNotes] = useState('');
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [currentTextPage, setCurrentTextPage] = useState(0);
  const [pageStartIndices, setPageStartIndices] = useState<number[]>([0]);
  const readingTextAreaRef = useRef<HTMLDivElement>(null);
  const readingMeasureRef = useRef<HTMLDivElement>(null);
  const modeKey = getModeCanonicalKey(mode);
  const isLibraryWorkspaceMode = modeKey === 'serbest-okuma';
  const isFreeReadingMode = isLibraryWorkspaceMode;
  const isMeasuredTextMode = !isLibraryWorkspaceMode;
  const [activeContent, setActiveContent] = useState<RecommendedStoryContent | null>(() => (
    recommendedContent ? shared.normalizeRecommendedStoryContent(recommendedContent) : null
  ));
  const [settings, setSettings] = useState<{
    wordsPerMinute: number;
    paceMs?: number;
    speedStep: number;
    metronomeEnabled: boolean;
    metronomeBpm: number;
    stopwatchEnabled: boolean;
    difficultyLevel: GroupingSettings['difficultyLevel'];
    fontSize: GroupingSettings['fontSize'];
    fontFamily: string;
    accentColor: string;
    enableCountdown: boolean;
    incompleteTextMaxDots: number;
  }>({
    wordsPerMinute: profile?.currentSpeed || 220,
    speedStep: 25,
    metronomeEnabled: isLibraryWorkspaceMode || modeKey === 'odaklanarak-okuma',
    metronomeBpm: 60,
    stopwatchEnabled: true,
    difficultyLevel: normalizeDifficultyLevel(profile?.readingLevel),
    fontSize: 'buyuk',
    fontFamily: 'GoogleSans',
    accentColor: 'default',
    enableCountdown: true,
    incompleteTextMaxDots: 3,
  });

  const readingText = (activeContent?.content || TEXT_READING_SAMPLE_TEXT).trim();
  const isUploadedContent = Boolean(
    activeContent?.displayMode === 'uploaded-file' ||
    activeContent?.displayMode === 'local-upload' ||
    activeContent?.id?.startsWith('local-')
  );
  const isStoredPdfContent = activeContent?.displayMode === 'stored-file';
  const storedPdfPageCount = Math.max(1, Number(activeContent?.sourceFile?.pageCount) || 1);
  const storedPdfPages = Array.isArray(activeContent?.sourceFile?.pages)
    ? activeContent.sourceFile.pages
      .filter((page) => page?.imageUrl)
      .sort((left, right) => Number(left?.pageNumber || 0) - Number(right?.pageNumber || 0))
    : [];
  const storedPdfUrl = activeContent?.sourceFile?.url
    ? `${activeContent.sourceFile.url}#toolbar=0&navpanes=0`
    : null;
  const incompleteTextPresentation = activeContent?.presentation?.incompleteText || null;
  const effectiveIncompleteTextMaxDots = Math.max(1, Math.min(3, Math.round(Number(settings.incompleteTextMaxDots) || 3)));
  const readingDisplayText = modeKey === 'eksiltili-metin' && incompleteTextPresentation?.displayText
    ? incompleteTextPresentation.displayText.trim()
    : readingText;
  const readingParagraphs = useMemo(() => splitReadingParagraphs(readingDisplayText), [readingDisplayText]);
  const totalTextPages = Math.max(1, pageStartIndices.length);
  const pageParaStart = pageStartIndices[currentTextPage] ?? 0;
  const pageParaEnd = pageStartIndices[currentTextPage + 1] ?? readingParagraphs.length;
  const currentPageParagraphs = readingParagraphs.slice(pageParaStart, pageParaEnd);
  const reviewQuestions = activeContent?.questions?.length
    ? activeContent.questions
    : (isFreeReadingMode || isUploadedContent ? [] : TEXT_READING_REVIEW_QUESTIONS);
  const textWordCount = isStoredPdfContent ? 0 : readingText.replace(/\s+/g, ' ').trim().split(' ').length;
  const hiddenLetterCount = Number(incompleteTextPresentation?.hiddenLetterCount) || 0;
  const minimumRecommendedWordCount = getTextExerciseMinimumWordCount(activeContent?.difficultyLevel || settings.difficultyLevel || profile?.readingLevel);
  const introParagraphs = modeKey === 'eksiltili-metin'
    ? [
      'Bu bölümde metindeki bazı harfler nokta ile gizlenir. Metni okurken eksik harfleri zihinsel olarak tamamlayarak ilerlersiniz.',
      'Metni tamamladığınızda sorulara geçerek hem okuma hızınızı hem de eksiltili metindeki anlama oranınızı ölçebilirsiniz.',
    ]
    : modeKey === 'anlama-calismalari'
      ? [
        'Bu bölümde odak metni anlayarak okumaktır. Metni tamamladıktan sonra anlama sorularına geçerek ana fikir ve çıkarım gücünüz ölçülür.',
        'Süre takibi kronometre ile yapılır; metni hazır olduğunuz anda sorulara geçirerek kendi doğal okuma hızınızla ilerlersiniz.',
      ]
      : modeKey === 'odaklanarak-okuma'
        ? [
          'Bu bölüm, dikkat dağılmadan tek metin üzerinde kalmanızı destekler. Metronom varsayılan olarak açıktır ve ritmi korumanıza yardım eder.',
          'Kronometre çalışırken metni odakla okuyabilir, istediğiniz anda sorulara geçerek hem hızınızı hem anlamanızı ölçebilirsiniz.',
        ]
        : isLibraryWorkspaceMode
          ? [
            'Kütüphaneden okumak istediğiniz metni seçebilirsiniz.',
            'Metronom, kronometre, hız ölçeği, anlama ölçeği ve not alanıyla gündelik okumalarınızı ya da soru çözümlerinizi tek ekranda takip edebilirsiniz.',
          ]
          : [
            'Bu bölüm, seçtiğiniz metni sabit biçimde okuyup süre takibi yapmanızı sağlar.',
            'Metni tamamladığınızda sorulara geçerek hem dakikadaki kelime hızınızı hem de anlama oranınızı birlikte görebilirsiniz.',
          ];
  const activeTitle = activeContent?.title?.trim() || 'Önerilen Metin';
  const activeCategory = activeContent?.category?.name || null;
  const activeTextType = activeContent?.textType || null;
  const activeQuestionCount = reviewQuestions.length;
  const hasReviewQuestions = reviewQuestions.length > 0;
  const readingPreview = isStoredPdfContent
    ? 'PDF dogrudan goruntulenecek. Sayfa ileri ve geri kontrolleri ile okumaya devam edebilirsiniz.'
    : `${readingDisplayText.slice(0, 360).trim()}${readingDisplayText.length > 360 ? '...' : ''}`;
  const isBusy = (contentLoading && phase === 'idle') || selectedContentLoading;
  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || profile?.name || 'Kullanıcı';
  const userImage = user?.avatar || user?.media?.url || user?.profilePhoto?.url || null;
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part: string) => part.charAt(0).toUpperCase())
    .join('');
  const hasAnsweredAllReviewQuestions = reviewQuestions.every((question) => Boolean(selectedAnswers[question.id]));
  const reviewCorrectCount = reviewQuestions.filter((question) => selectedAnswers[question.id] === question.correctKey).length;
  const reviewRatio = reviewQuestions.length > 0 ? reviewCorrectCount / reviewQuestions.length : 0;
  const measuredWpm = Math.max(0, Math.round((textWordCount / Math.max(elapsedSeconds, 1)) * 60));
  // Ölçülen hız ancak metin bitince anlam taşır: measuredWpm metnin TAMAMINI okunmuş sayar,
  // okuma sürerken (öğrencinin metinde nerede olduğu bilinmediğinden) fahiş değer verir.
  // Bu yüzden okuma boyunca hedef hız, sorulara geçildiğinde ölçülen hız gösterilir.
  const showMeasuredSpeed = isMeasuredTextMode && phase === 'review' && elapsedSeconds > 0;
  const recommendedWpm = reviewRatio >= 0.8
    ? measuredWpm
    : reviewRatio >= 0.5
      ? Math.max(80, measuredWpm - 25)
      : Math.max(60, measuredWpm - 50);
  const reviewSummary = isLibraryWorkspaceMode && !hasReviewQuestions
    ? 'Kütüphane çalışmanız süre, okuma hızı, kişisel ölçekler ve notlarınızla kaydedildi.'
    : reviewRatio >= 0.8
      ? 'Hem hız hem de anlama açısından güçlü bir performans gösterdiniz.'
      : reviewRatio >= 0.5
        ? 'Okuma hızınız iyi, anlama tarafı biraz daha güçlendikçe daha sağlıklı ölçüm alınır.'
        : 'Metni tamamlamış olsanız da anlama seviyesi düştüğü için biraz daha kontrollü hız öneriyoruz.';

  useEffect(() => {
    if (phase === 'review') setActiveReviewQuestionIndex(0);
  }, [phase, reviewQuestions.length]);

  const primaryButtonLabel = phase === 'review'
    ? (reviewSubmitted ? 'Modlara Dön' : 'Egzersizi Tamamla')
    : phase === 'reading' || phase === 'countdown'
      ? 'Durdur'
      : phase === 'paused'
        ? 'Devam Et'
        : 'Başlat';
  const timeLabel = formatClock(elapsedSeconds);
  const activeAccent = resolveAccentColor(settings.accentColor);
  const activeFontFamily = FONT_FAMILY_OPTIONS.find((item) => item.value === settings.fontFamily)?.css || settings.fontFamily;
  const isFullscreenReadingPhase = isFullscreen && (phase === 'countdown' || phase === 'reading' || phase === 'paused');
  const activeTextReadingFontSize = isFullscreenReadingPhase
    ? TEXT_READING_FULLSCREEN_FONT_SIZE_CLASS[settings.fontSize]
    : TEXT_READING_FONT_SIZE_CLASS[settings.fontSize];
  const stopMetronome = useCallback(() => {
    if (metronomeTimerRef.current !== null) {
      window.clearInterval(metronomeTimerRef.current);
      metronomeTimerRef.current = null;
    }
  }, []);

  const playMetronomeBeat = useCallback(() => {
    if (muted || typeof window === 'undefined') return;

    if (!metronomeAudioRef.current) {
      metronomeAudioRef.current = new Audio(METRONOME_AUDIO_SRC);
      metronomeAudioRef.current.preload = 'auto';
    }

    metronomeAudioRef.current.currentTime = 0;
    metronomeAudioRef.current.play().catch(() => { });
  }, [muted]);

  const resetExercise = useCallback(() => {
    stopMetronome();
    setPhase('idle');
    setCountdown(3);
    setElapsedSeconds(0);
    setSelectedAnswers({});
    setReviewSubmitted(false);
    setShowCompletionSummary(false);
    setUploadError(null);
    setCurrentTextPage(0);
    setPageStartIndices([0]);
  }, [stopMetronome]);

  // Reset pagination when font size/family or content changes mid-reading
  useEffect(() => {
    setCurrentTextPage(0);
    setPageStartIndices([0]);
  }, [activeTextReadingFontSize, activeFontFamily, readingDisplayText]);

  const measureReadingFit = useCallback(() => {
    const textArea = readingTextAreaRef.current;
    const measure = readingMeasureRef.current;
    if (!textArea || !measure) return;

    const availH = textArea.clientHeight;
    const badgeEl = measure.querySelector('[data-badge]') as HTMLElement | null;
    const badgeH = badgeEl ? badgeEl.offsetHeight + 20 : 0; // mb-5 = 20px
    const gapH = 24; // mt-6 = 1.5rem at 16px root

    const paraEls = Array.from(measure.querySelectorAll('[data-para]')) as HTMLElement[];
    if (paraEls.length === 0) return;

    // Iteratively assign paragraphs to pages based on actual rendered heights.
    // Page 0 reserves badgeH; subsequent pages start fresh.
    const boundaries: number[] = [0];
    let pageStartIdx = 0;
    let cumH = badgeH;

    for (let i = 0; i < paraEls.length; i++) {
      const h = paraEls[i].offsetHeight;
      const gap = i === pageStartIdx ? 0 : gapH;

      if (i !== pageStartIdx && cumH + gap + h > availH) {
        boundaries.push(i);
        pageStartIdx = i;
        cumH = h;
      } else {
        cumH += gap + h;
      }
    }

    setPageStartIndices(boundaries);
    setCurrentTextPage(prev => Math.min(prev, boundaries.length - 1));
  }, []);

  useLayoutEffect(() => {
    if (phase === 'reading') measureReadingFit();
  }, [phase, measureReadingFit, activeTextReadingFontSize, activeFontFamily]);

  useEffect(() => {
    if (phase !== 'reading') return;
    const el = readingTextAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(measureReadingFit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [phase, measureReadingFit]);

  const fetchLibraryItems = useCallback(async () => {
    try {
      setLibraryLoading(true);
      const query = new URLSearchParams({
        limit: '200',
      });

      if (librarySearch.trim()) query.set('search', librarySearch.trim());
      if (libraryTextType !== 'all') query.set('textType', libraryTextType);

      const response = await ApiService.getApi(`speed-reading/content/library?${query.toString()}`);
      const items: LibraryContentItem[] = Array.isArray(response?.items) ? response.items as LibraryContentItem[] : [];
      const levelGroups: LibraryLevelGroup[] = Array.isArray(response?.levels)
        ? response.levels.map((levelGroup: LibraryLevelGroup) => ({
          ...levelGroup,
          items: Array.isArray(levelGroup.items)
            ? (isMeasuredTextMode
              ? levelGroup.items.filter((item) => Number(item?.wordCount) >= minimumRecommendedWordCount)
              : levelGroup.items)
            : [],
        }))
        : [];
      const filteredItems = isMeasuredTextMode
        ? items.filter((item) => Number(item?.wordCount) >= minimumRecommendedWordCount)
        : items;
      const groupedItems = levelGroups.flatMap((levelGroup) => levelGroup.items);

      setLibraryLevelGroups(levelGroups);
      setLibraryItems(groupedItems.length > 0 ? groupedItems : filteredItems);
    } catch (error) {
      console.error('Metin kütüphanesi yüklenemedi:', error);
      setLibraryItems([]);
      setLibraryLevelGroups([]);
    } finally {
      setLibraryLoading(false);
    }
  }, [isMeasuredTextMode, librarySearch, libraryTextType, minimumRecommendedWordCount]);

  const handleSelectLibraryItem = useCallback(async (contentId: string) => {
    try {
      setSelectedContentLoading(true);
      const query = new URLSearchParams({
        includeAnswers: 'true',
        mode: getModeQueryValue(mode),
      });
      if (getModeCanonicalKey(mode) === 'eksiltili-metin') {
        query.set('incompleteTextMaxDots', String(effectiveIncompleteTextMaxDots));
      }
      const response = await ApiService.getApi(`speed-reading/content/library/${contentId}?${query.toString()}`);
      const normalized = shared.normalizeRecommendedStoryContent(response);
      if (!normalized) return;

      resetExercise();
      setFreeReadingNotes('');
      setFreeReadingSpeedScore(3);
      setFreeReadingComprehensionScore(3);
      setCurrentPdfPage(1);
      setActiveContent(normalized);
      lastRecommendedContentIdRef.current = normalized.id || null;
      setLibraryOpen(false);
    } catch (error) {
      console.error('Seçilen metin yüklenemedi:', error);
    } finally {
      setSelectedContentLoading(false);
    }
  }, [effectiveIncompleteTextMaxDots, mode, resetExercise]);


  const handleStoredPdfScroll = useCallback(() => {
    const container = storedPdfScrollRef.current;
    if (!container || storedPdfPages.length === 0) return;

    const scrollAnchor = container.scrollTop + (container.clientHeight * 0.35);
    let nextPage = Number(storedPdfPages[0]?.pageNumber) || 1;

    storedPdfPages.forEach((page, index) => {
      const pageNumber = Number(page?.pageNumber) || (index + 1);
      const pageNode = storedPdfPageRefs.current[pageNumber];
      if (!pageNode) return;
      if (pageNode.offsetTop <= scrollAnchor) nextPage = pageNumber;
    });

    setCurrentPdfPage((prev) => (prev === nextPage ? prev : nextPage));
  }, [storedPdfPages]);


  useEffect(() => {
    setSettings((prev) => {
      const applied = applySharedExerciseSettings(prev);
      return {
        ...applied,
        metronomeBpm: normalizeMetronomeBpm(applied.metronomeBpm),
      };
    });
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;
    persistSharedExerciseSettings(settings);
  }, [settings, settingsOpen]);

  useEffect(() => {
    const normalized = recommendedContent ? shared.normalizeRecommendedStoryContent(recommendedContent) : null;
    if (!normalized) return;

    setActiveContent((prev) => {
      if (!prev || prev.id === lastRecommendedContentIdRef.current) {
        return normalized;
      }
      return prev;
    });
    lastRecommendedContentIdRef.current = normalized.id || null;
  }, [recommendedContent]);

  useEffect(() => {
    setCurrentPdfPage(1);
    if (storedPdfScrollRef.current) storedPdfScrollRef.current.scrollTop = 0;
  }, [activeContent?.id]);

  useEffect(() => {
    if (modeKey !== 'eksiltili-metin') return;
    const contentId = activeContent?.id;
    if (!contentId || contentId.startsWith('local-')) return;

    const currentMaxDots = Number(activeContent?.presentation?.incompleteText?.maxDotsPerWord) || 3;
    if (currentMaxDots === effectiveIncompleteTextMaxDots) return;

    let cancelled = false;
    setSelectedContentLoading(true);

    const query = new URLSearchParams({
      includeAnswers: 'true',
      mode: getModeQueryValue(mode),
      incompleteTextMaxDots: String(effectiveIncompleteTextMaxDots),
    });

    ApiService.getApi(`speed-reading/content/library/${contentId}?${query.toString()}`)
      .then((response: any) => {
        if (cancelled) return;
        const normalized = shared.normalizeRecommendedStoryContent(response);
        if (normalized) setActiveContent(normalized);
      })
      .catch((error) => {
        if (!cancelled) console.error('Eksiltili metin nokta ayarı uygulanamadı:', error);
      })
      .finally(() => {
        if (!cancelled) setSelectedContentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeContent?.id,
    activeContent?.presentation?.incompleteText?.maxDotsPerWord,
    effectiveIncompleteTextMaxDots,
    mode,
    modeKey,
  ]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (phase !== 'countdown' || countdown <= 0) return;

    const timer = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'countdown' || countdown !== 0) return;
    setPhase('reading');
    setElapsedSeconds(0);
  }, [phase, countdown]);

  useEffect(() => {
    if (phase !== 'reading') return;

    const timer = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (!libraryOpen) return;
    void fetchLibraryItems();
  }, [libraryOpen, fetchLibraryItems]);

  useEffect(() => {
    if (phase !== 'reading' || !settings.metronomeEnabled || muted) {
      stopMetronome();
      return;
    }

    playMetronomeBeat();
    const beatInterval = getMetronomeIntervalMs(settings.metronomeBpm);
    metronomeTimerRef.current = window.setInterval(() => {
      playMetronomeBeat();
    }, beatInterval);

    return () => {
      stopMetronome();
    };
  }, [muted, phase, playMetronomeBeat, settings.metronomeBpm, settings.metronomeEnabled, stopMetronome]);

  useEffect(() => {
    return () => {
      stopMetronome();
      if (metronomeAudioRef.current) {
        metronomeAudioRef.current.pause();
        metronomeAudioRef.current = null;
      }
    };
  }, [stopMetronome]);

  const handleCloseExercise = async () => {
    if (document.fullscreenElement === fullscreenContainerRef.current) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Tam ekrandan çıkılamadı:', error);
      }
    }

    resetExercise();
    onClose();
  };

  const handleToggleFullscreen = async () => {
    const container = fullscreenContainerRef.current;
    if (!container) return;

    try {
      if (document.fullscreenElement === container) {
        await document.exitFullscreen();
        return;
      }

      await container.requestFullscreen();
    } catch (error) {
      console.error('Tam ekran değiştirilemedi:', error);
    }
  };

  const handleStart = async () => {
    if (isBusy) return;
    if (phase === 'paused') {
      await requestExerciseFullscreen(fullscreenContainerRef.current);
      setPhase('reading');
      return;
    }

    await requestExerciseFullscreen(fullscreenContainerRef.current);
    setSelectedAnswers({});
    setReviewSubmitted(false);
    setElapsedSeconds(0);
    setCountdown(3);
    if (settings.enableCountdown) {
      setPhase('countdown');
      return;
    }

    setPhase('reading');
  };

  const handlePause = async () => {
    if (phase === 'countdown' || phase === 'reading') {
      setPhase('paused');
    }
  };

  const handlePrimaryAction = async () => {
    if (phase === 'review') {
      if (!reviewSubmitted) {
        if (!hasAnsweredAllReviewQuestions) return;
        reportResult({
          questionCount: reviewQuestions.length || undefined,
          correctCount: reviewQuestions.length ? reviewCorrectCount : undefined,
          wpm: showMeasuredSpeed ? measuredWpm : undefined,
          wordCount: textWordCount || undefined,
          contentId: activeContent?.id || null,
        });
        setReviewSubmitted(true);
        setShowCompletionSummary(true);
        return;
      }

      await handleCloseExercise();
      return;
    }

    if (phase === 'reading' || phase === 'countdown') {
      await handlePause();
      return;
    }

    await handleStart();
  };

  const handleFinishFreeReading = () => {
    stopMetronome();
    setPhase('paused');
    setShowCompletionSummary(true);

    if (typeof window === 'undefined') return;

    try {
      const storageKey = 'speed-reading-free-reading-logs';
      const currentLogs = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      const nextLog = {
        id: `free-reading-${Date.now()}`,
        title: activeTitle,
        textType: activeTextType,
        category: activeCategory,
        wordCount: textWordCount,
        elapsedSeconds,
        measuredWpm,
        speedScore: freeReadingSpeedScore,
        comprehensionScore: freeReadingComprehensionScore,
        notes: freeReadingNotes.trim(),
        createdAt: new Date().toISOString(),
      };
      const logs = Array.isArray(currentLogs) ? currentLogs : [];
      window.localStorage.setItem(storageKey, JSON.stringify([nextLog, ...logs].slice(0, 50)));
    } catch (error) {
      console.error('Serbest okuma notu kaydedilemedi:', error);
    }
  };

  const visibleLibraryLevelGroups = libraryLevelGroups.filter((levelGroup) => levelGroup.items.length > 0);
  const renderLibraryItemCard = (item: LibraryContentItem) => {
    const isSelected = item.id === activeContent?.id;

    return (
      <button
        key={item.id}
        onClick={() => { void handleSelectLibraryItem(item.id); }}
        disabled={selectedContentLoading}
        className={`rounded-2xl border p-5 text-left transition-all ${selectedContentLoading ? 'opacity-60 cursor-not-allowed' : 'hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(24,72,92,0.08)]'}`}
        style={{
          borderColor: isSelected ? 'color-mix(in srgb, var(--theme-primary) 25%, white)' : '#e4edf1',
          backgroundColor: isSelected ? 'color-mix(in srgb, var(--theme-primary) 8%, white)' : '#fff',
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          {item.textType && (
            <span className="rounded-full bg-[#edf6fa] px-2.5 py-1 text-[11px] font-semibold text-[#2d7087]">
              {item.textType}
            </span>
          )}
          {item.category?.name && (
            <span className="rounded-full bg-[#f2f6f8] px-2.5 py-1 text-[11px] font-semibold text-[#677e88]">
              {item.category.name}
            </span>
          )}
          {item.difficultyLevel && (
            <span className="rounded-full bg-[#eef5f3] px-2.5 py-1 text-[11px] font-semibold text-[#567a6b]">
              {item.difficultyLevel}
            </span>
          )}
        </div>
        <h5 className="mt-3 text-lg font-black text-[#1e4c5d]">{item.title}</h5>
        {item.summary && (
          <p className="mt-2 text-sm leading-6 text-[#5d7681]">{item.summary}</p>
        )}
        <div className="mt-4 flex flex-wrap gap-3 text-[12px] font-semibold text-[#728892]">
          <span>{item.wordCount || 0} kelime</span>
          <span>{item.questionCount || 0} soru</span>
          <span>{item.characterCount || 0} karakter</span>
        </div>
      </button>
    );
  };

  return (
    <div
      ref={fullscreenContainerRef}
      className={`${isFullscreenReadingPhase ? 'h-screen p-0' : isFullscreen ? 'h-screen p-3' : 'h-full p-6'} bg-[#f3f5f7]`}
    >
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full`}>
        <div className={`h-full overflow-hidden bg-[#f8fafb] flex gap-5 relative ${isFullscreenReadingPhase ? 'border-0 rounded-none px-4 py-4' : 'border border-[#e6edf1] rounded-xl px-6 py-5'}`}>
          <div className="min-h-0 flex-1 flex flex-col">
            <div className={`flex items-center justify-between ${isFullscreenReadingPhase ? 'mb-2' : 'mb-4'}`}>
              <div className="flex items-center gap-2">
                <ScanText className="w-[19px] h-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[20px] font-bold" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setLibraryOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#d9e3e8] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#55707d] hover:text-[#2f5563]"
                >
                  <Library className="w-3.5 h-3.5" />
                  Metin Seç
                </button>
                <button onClick={() => setMuted((m) => !m)} className={EXERCISE_TOOL_BUTTON_CLASS} title={muted ? 'Sesi Aç' : 'Sesi Kapat'}>
                  {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <button onClick={() => setSettingsOpen(true)} className={EXERCISE_TOOL_BUTTON_CLASS} title="Ayarlar">
                  <Settings2 className="w-5 h-5" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
                  {isFullscreen ? <Shrink className="w-5 h-5" /> : <Expand className="w-5 h-5" />}
                </button>
                <button onClick={() => { void handleCloseExercise(); }} className={EXERCISE_TOOL_BUTTON_CLASS} title="Kapat">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className={`relative min-h-0 flex-1 bg-white overflow-hidden ${isFullscreenReadingPhase ? 'rounded-none border-0 px-8 py-6' : 'border border-[#e8edf0] rounded-[10px] px-6 py-5'}`}>
              {selectedContentLoading && (
                <div className="absolute left-6 right-6 top-5 z-10 rounded-2xl border border-[#dce8ee] bg-white/90 px-4 py-3 shadow-sm backdrop-blur">
                  <div className="flex items-center gap-3 text-sm font-semibold text-[#295567]">
                    <div className="w-4 h-4 border-2 border-[#2f5563] border-t-transparent rounded-full animate-spin" />
                    Seçilen metin hazırlanıyor...
                  </div>
                </div>
              )}

              {phase === 'review' && (
                <ReviewQuestionsPager
                  questions={reviewQuestions as ReviewPagerQuestion[]}
                  selectedAnswers={selectedAnswers}
                  setSelectedAnswers={setSelectedAnswers}
                  reviewSubmitted={reviewSubmitted}
                  activeQuestionIndex={activeReviewQuestionIndex}
                  setActiveQuestionIndex={setActiveReviewQuestionIndex}
                  fontFamily={activeFontFamily}
                />
              )}

              {(phase === 'countdown' || phase === 'paused') && (
                <div className="h-full flex flex-col items-center justify-center gap-3">
                  {phase === 'paused'
                    ? <Pause className="w-20 h-20 text-[#2f5563]" strokeWidth={2.4} />
                    : <span className="text-7xl font-extrabold text-[#18485c]">{countdown}</span>}
                </div>
              )}

              {phase === 'idle' && (
                <div className={`h-full min-h-0 ${isStoredPdfContent ? '' : 'flex items-center justify-center'}`}>
                  <div className={`${isStoredPdfContent ? 'h-full w-full min-h-0' : 'max-w-[980px] w-full'}`}>
                    {contentLoading ? (
                      <div className="mx-auto max-w-[900px] flex flex-col items-center gap-4">
                        <div className="w-9 h-9 border-2 border-[#2f5563] border-t-transparent rounded-full animate-spin" />
                        <p className="text-[20px] font-bold leading-[1.55] text-[#2e6276]">İçerik yükleniyor...</p>
                      </div>
                    ) : isStoredPdfContent ? (
                      <div className="h-full min-h-0">
                        {uploadError && (
                          <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                            {uploadError}
                          </div>
                        )}

                        <div className="h-full overflow-hidden bg-white">
                          {storedPdfPages.length > 0 ? (
                            <div
                              ref={storedPdfScrollRef}
                              onScroll={handleStoredPdfScroll}
                              className="h-full overflow-y-auto px-2 py-2"
                            >
                              <div className="mx-auto flex max-w-[980px] flex-col gap-5">
                                {storedPdfPages.map((page, index) => {
                                  const pageNumber = Number(page?.pageNumber) || (index + 1);
                                  return (
                                    <div
                                      key={String(page.id || pageNumber)}
                                      ref={(node) => {
                                        storedPdfPageRefs.current[pageNumber] = node;
                                      }}
                                      className="overflow-hidden rounded-[18px] border border-[#dde7eb] bg-white shadow-[0_10px_28px_rgba(26,72,92,0.08)]"
                                    >
                                      <img
                                        src={String(page.imageUrl || '')}
                                        alt={`${activeTitle} sayfa ${pageNumber}`}
                                        className="block h-auto w-full"
                                        loading={pageNumber <= 2 ? 'eager' : 'lazy'}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : storedPdfUrl ? (
                            <iframe
                              key={storedPdfUrl}
                              src={storedPdfUrl}
                              title={activeTitle}
                              className="h-full w-full bg-white"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center px-6 text-center text-sm font-semibold text-[#6d838e]">
                              PDF goruntulenemedi.
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mx-auto max-w-[940px] rounded-[28px] border border-[#e4edf1] bg-[#fbfdfe] p-8 shadow-[0_18px_50px_rgba(26,72,92,0.06)]">
                        <div className="flex flex-wrap items-center gap-2">
                          {activeTextType && (
                            <span className="rounded-full bg-[#edf6fa] px-3 py-1 text-xs font-semibold text-[#2d7087]">
                              {activeTextType}
                            </span>
                          )}
                          {activeCategory && (
                            <span className="rounded-full bg-[#f1f5f7] px-3 py-1 text-xs font-semibold text-[#667d88]">
                              {activeCategory}
                            </span>
                          )}
                          <span className="rounded-full bg-[#f7f2ea] px-3 py-1 text-xs font-semibold text-[#8c6c45]">
                            {textWordCount} kelime
                          </span>
                          {!isFreeReadingMode && activeQuestionCount > 0 && (
                            <span className="rounded-full bg-[#eef5f3] px-3 py-1 text-xs font-semibold text-[#557f73]">
                              {activeQuestionCount} soru
                            </span>
                          )}
                          {modeKey === 'eksiltili-metin' && hiddenLetterCount > 0 && (
                            <span className="rounded-full bg-[#edf6fa] px-3 py-1 text-xs font-semibold text-[#2d7087]">
                              {hiddenLetterCount} eksik harf
                            </span>
                          )}
                        </div>

                        <h3 className="mt-5 text-[34px] leading-tight font-black text-[#194e62]">{activeTitle}</h3>
                        <p className="mt-4 text-[17px] leading-8 text-[#111111]">{readingPreview}</p>

                        <div className="mt-6 grid gap-4 md:grid-cols-2">
                          {introParagraphs.map((paragraph) => (
                            <div key={paragraph} className="rounded-2xl border border-[#e6eef2] bg-white px-5 py-4 text-[15px] leading-7 text-[#5a7380]">
                              {paragraph}
                            </div>
                          ))}
                        </div>

                        {uploadError && (
                          <div className="mt-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                            {uploadError}
                          </div>
                        )}

                        <div className="mt-6 flex flex-wrap gap-3">
                          <button
                            onClick={() => setLibraryOpen(true)}
                            className="inline-flex items-center gap-2 rounded-xl border border-[#d6e2e8] bg-white px-5 py-3 text-sm font-semibold text-[#315869] hover:bg-[#f7fbfd]"
                          >
                            <Library className="w-4 h-4" />
                            Metin Seç
                          </button>
                          <button
                            onClick={() => { void handleStart(); }}
                            disabled={isBusy}
                            className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white ${isBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
                            style={getThemeChromeButtonStyle()}
                          >
                            <Play className="w-4 h-4" />
                            Başlat
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {phase === 'reading' && isStoredPdfContent && (
                <div className="h-full min-h-0 overflow-hidden">
                  <div className="h-full overflow-hidden bg-white">
                    {storedPdfPages.length > 0 ? (
                      <div
                        ref={storedPdfScrollRef}
                        onScroll={handleStoredPdfScroll}
                        className={`h-full overflow-y-auto ${isFullscreenReadingPhase ? 'px-8 py-4' : 'px-2 py-2'}`}
                      >
                        <div className={`mx-auto flex flex-col gap-5 ${isFullscreenReadingPhase ? 'max-w-[1180px]' : 'max-w-[980px]'}`}>
                          {storedPdfPages.map((page, index) => {
                            const pageNumber = Number(page?.pageNumber) || (index + 1);
                            return (
                              <div
                                key={String(page.id || pageNumber)}
                                ref={(node) => {
                                  storedPdfPageRefs.current[pageNumber] = node;
                                }}
                                className="overflow-hidden rounded-[18px] border border-[#dde7eb] bg-white shadow-[0_10px_28px_rgba(26,72,92,0.08)]"
                              >
                                <img
                                  src={String(page.imageUrl || '')}
                                  alt={`${activeTitle} sayfa ${pageNumber}`}
                                  className="block h-auto w-full"
                                  loading={pageNumber <= 2 ? 'eager' : 'lazy'}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : storedPdfUrl ? (
                      <iframe
                        key={storedPdfUrl}
                        src={storedPdfUrl}
                        title={`${activeTitle} PDF`}
                        className="h-full w-full bg-white"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-6 text-center text-sm font-semibold text-[#6d838e]">
                        PDF goruntulenemedi.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {phase === 'reading' && !isStoredPdfContent && (
                <div className="flex h-full min-h-0 flex-col gap-3">
                  <div
                    ref={readingTextAreaRef}
                    className={`relative min-h-0 flex-1 overflow-hidden font-semibold text-[#111111] ${isFullscreenReadingPhase ? 'mx-auto max-w-[1180px] px-4' : ''} ${activeTextReadingFontSize}`}
                    style={{ fontFamily: activeFontFamily }}
                  >
                    {/* Hidden measurement div – all paragraphs rendered to calculate how many fit */}
                    <div
                      ref={readingMeasureRef}
                      className={`pointer-events-none absolute inset-0 overflow-visible opacity-0 ${isFullscreenReadingPhase ? 'px-4' : ''}`}
                      aria-hidden="true"
                    >
                      {modeKey !== 'eksiltili-metin' && (
                        <div data-badge className="mb-5 rounded-2xl border border-[#e7edf0] bg-[#fbfdfe] px-5 py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-[#edf6fa] px-3 py-1 text-xs font-semibold text-[#2d7087]">
                              {activeTitle}
                            </span>
                          </div>
                        </div>
                      )}
                      {readingParagraphs.map((paragraph, index) => (
                        <p key={index} data-para className={index > 0 ? 'mt-6' : ''}>
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    {modeKey !== 'eksiltili-metin' && currentTextPage === 0 && (
                      <div className="mb-5 rounded-2xl border border-[#e7edf0] bg-[#fbfdfe] px-5 py-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-[#edf6fa] px-3 py-1 text-xs font-semibold text-[#2d7087]">
                            {activeTitle}
                          </span>
                          {activeTextType && (
                            <span className="rounded-full bg-[#f2f6f8] px-3 py-1 text-xs font-semibold text-[#667d88]">
                              {activeTextType}
                            </span>
                          )}
                          {activeCategory && (
                            <span className="rounded-full bg-[#eef5f3] px-3 py-1 text-xs font-semibold text-[#57796c]">
                              {activeCategory}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {currentPageParagraphs.map((paragraph, index) => (
                      <p key={`${currentTextPage}-${index}-${paragraph.slice(0, 20)}`} className={index > 0 ? 'mt-6' : ''}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                  {totalTextPages > 1 && (
                    <div className="flex shrink-0 items-center justify-center gap-2 border-t border-slate-100 pt-3">
                      <button
                        type="button"
                        onClick={() => setCurrentTextPage((p) => Math.max(0, p - 1))}
                        disabled={currentTextPage === 0}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-30"
                        aria-label="Önceki sayfa"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <span className="min-w-[4rem] text-center text-xs font-semibold text-slate-500">
                        {currentTextPage + 1} / {totalTextPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentTextPage((p) => Math.min(totalTextPages - 1, p + 1))}
                        disabled={currentTextPage === totalTextPages - 1}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-50 disabled:opacity-30"
                        aria-label="Sonraki sayfa"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {isFullscreenReadingPhase && (
            <div className="absolute bottom-5 right-5 z-30 flex items-center gap-2 rounded-full border border-[#dbe7ec] bg-white/92 px-2 py-2 shadow-[0_16px_45px_rgba(26,72,92,0.18)] backdrop-blur">
              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={isBusy}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-full px-5 text-sm font-bold text-white disabled:opacity-60"
                style={getThemeChromeButtonStyle()}
                type="button"
              >
                {(primaryButtonLabel === 'Başlat' || primaryButtonLabel === 'Devam Et') && !isBusy && <Play className="w-4 h-4" />}
                {primaryButtonLabel === 'Durdur' && <Pause className="w-4 h-4" />}
                {isBusy ? 'İçerik Yükleniyor...' : primaryButtonLabel}
              </button>

              {hasReviewQuestions && (phase === 'paused' || phase === 'reading') && (
                <button
                  onClick={() => setPhase('review')}
                  className="inline-flex h-10 items-center justify-center rounded-full bg-[#24465a] px-5 text-sm font-bold text-white hover:bg-[#1c394a]"
                  type="button"
                >
                  Metni Bitirdim
                </button>
              )}

              {!hasReviewQuestions && (phase === 'reading' || phase === 'paused') && (
                <button
                  onClick={() => {
                    if (isFreeReadingMode) {
                      handleFinishFreeReading();
                      return;
                    }
                    void handleCloseExercise();
                  }}
                  className="inline-flex h-10 items-center justify-center rounded-full bg-[#24465a] px-5 text-sm font-bold text-white hover:bg-[#1c394a]"
                  type="button"
                >
                  Çalışmayı Bitir
                </button>
              )}
            </div>
          )}

          <aside className={`${isFullscreenReadingPhase ? 'hidden' : 'hidden lg:flex'} w-[320px] shrink-0 h-full min-h-0 rounded-xl border border-[#e0eaee] bg-white p-5 flex-col`}>
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <div className="flex items-center gap-3">
                {userImage ? (
                  <img src={userImage} alt={userName} className="w-11 h-11 rounded-full object-cover bg-[#daf0f7]" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#daf0f7] text-[#146f8e] flex items-center justify-center font-bold">
                    {userInitials || 'K'}
                  </div>
                )}
                <div>
                  <div className="text-[#355c68] text-sm font-bold">{userName}</div>
                  <div className="text-[#94a8b3] text-xs">1. Seviye</div>
                </div>
              </div>

              <div className="mt-6 flex flex-col items-center text-center">
                <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="mt-4 text-[34px] leading-none font-black" style={getThemeChromeTextStyle()}>{mode.name}</h3>
              </div>

              {modeKey === 'eksiltili-metin' && (
                <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f8fbfc] p-4 text-center">
                  <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#8aa0aa]">Aktif Metin</div>
                  <div className="mt-2 text-[15px] font-black leading-6 text-[#194e62]">{activeTitle}</div>
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {activeTextType && (
                      <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#2d7087]">
                        {activeTextType}
                      </span>
                    )}
                    {activeCategory && (
                      <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#667d88]">
                        {activeCategory}
                      </span>
                    )}
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#8c6c45]">
                      {textWordCount} kelime
                    </span>
                  </div>
                  <button
                    onClick={() => setLibraryOpen(true)}
                    className="mt-3 text-[11px] font-bold text-[#2f6d85]"
                    type="button"
                  >
                    Metni Değiştir
                  </button>
                </div>
              )}

              {phase === 'idle' && (
                <SidebarPreparationCard paragraphs={introParagraphs} fontFamily={activeFontFamily} />
              )}

              {modeKey !== 'eksiltili-metin' && (
                <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-semibold text-[#6c8591]">Aktif Metin</div>
                    <button
                      onClick={() => setLibraryOpen(true)}
                      className="text-[11px] font-semibold text-[#2f6d85]"
                    >
                      Değiştir
                    </button>
                  </div>
                  <div className="mt-2 text-sm font-bold text-[#27596a]">{activeTitle}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#5b7784]">
                      {isStoredPdfContent ? `${storedPdfPageCount} sayfa` : `${textWordCount} kelime`}
                    </span>
                    {modeKey === 'eksiltili-metin' && hiddenLetterCount > 0 && (
                      <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#5b7784]">
                        {hiddenLetterCount} eksik harf
                      </span>
                    )}
                  </div>
                </div>
              )}

              {isFreeReadingMode && (
                <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4">
                  <div className="text-xs font-semibold text-[#6c8591]">Kütüphane Çalışma Kaydı</div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[12px] font-semibold text-[#5d7681]">
                      <span>Hız Ölçeği</span>
                      <span>{freeReadingSpeedScore}/5</span>
                    </div>
                    <div className="mt-2 grid grid-cols-5 gap-1.5">
                      {[1, 2, 3, 4, 5].map((score) => (
                        <button
                          key={`speed-${score}`}
                          onClick={() => setFreeReadingSpeedScore(score)}
                          className={`h-8 rounded-lg text-xs font-black transition-colors ${freeReadingSpeedScore === score ? 'text-white' : 'bg-white text-[#52707d] hover:bg-[#eef5f8]'}`}
                          style={freeReadingSpeedScore === score ? getThemeChromeButtonStyle() : undefined}
                          type="button"
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[12px] font-semibold text-[#5d7681]">
                      <span>Anlama Ölçeği</span>
                      <span>{freeReadingComprehensionScore}/5</span>
                    </div>
                    <div className="mt-2 grid grid-cols-5 gap-1.5">
                      {[1, 2, 3, 4, 5].map((score) => (
                        <button
                          key={`comprehension-${score}`}
                          onClick={() => setFreeReadingComprehensionScore(score)}
                          className={`h-8 rounded-lg text-xs font-black transition-colors ${freeReadingComprehensionScore === score ? 'text-white' : 'bg-white text-[#52707d] hover:bg-[#eef5f8]'}`}
                          style={freeReadingComprehensionScore === score ? getThemeChromeButtonStyle() : undefined}
                          type="button"
                        >
                          {score}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="mt-4 block text-[12px] font-semibold text-[#5d7681]" htmlFor="free-reading-notes">
                    Notlar
                  </label>
                  <textarea
                    id="free-reading-notes"
                    value={freeReadingNotes}
                    onChange={(event) => setFreeReadingNotes(event.target.value)}
                    className="mt-2 min-h-[92px] w-full resize-none rounded-xl border border-[#dce6eb] bg-white px-3 py-2 text-sm leading-6 text-[#244f60] outline-none focus:border-[#8fb8c8]"
                    placeholder="Okuma veya soru çözümü notları"
                  />
                </div>
              )}

              {phase === 'review' && (
                <div className="mt-4 rounded-xl border border-[#e4edf1] bg-[#f7fafc] p-4">
                  <div className="text-xs font-semibold text-[#6c8591]">Değerlendirme</div>
                  <div className="mt-2 text-sm font-bold text-[#27596a]">Metinle ilgili soruları cevaplayın.</div>
                  <p className="mt-2 text-[13px] leading-6 text-[#5d7681]">
                    Süreniz ve doğru cevap oranınız birlikte değerlendirilerek okuma hızınız ölçülür.
                  </p>
                </div>
              )}

              {phase === 'review' && reviewSubmitted && (
                <div className="mt-4 rounded-xl border border-[#dceaf0] bg-[#f7fbfd] p-4">
                  <div className="text-xs font-semibold text-[#6c8591]">Değerlendirme Kaydedildi</div>
                  <p className="mt-2 text-[13px] leading-6 text-[#446470]">
                    Sonuç özeti ekranın ortasında gösterilir. Modlara dönerek yeni çalışmaya geçebilirsiniz.
                  </p>
                </div>
              )}
            </div>

            <div className="shrink-0 pt-4">
              <div className="rounded-xl border border-[#e4edf1] bg-[#f8fbfc] px-4 py-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#7b909b]">
                  <Clock3 className="w-3.5 h-3.5" />
                  Kronometre
                </div>
                {settings.stopwatchEnabled ? (
                  <p className="text-[38px] leading-none font-black mt-2" style={getThemeChromeTextStyle()}>{timeLabel}</p>
                ) : (
                  <p className="mt-2 text-sm font-semibold text-[#667d88]">Kapalı, fakat ölçüm arka planda sürüyor.</p>
                )}
              </div>

              <div className="mt-3 rounded-xl border border-[#e4edf1] bg-[#f8fbfc] px-4 py-3">
                <div className="flex items-center gap-2 text-xs font-semibold text-[#7b909b]">
                  <TimerReset className="w-3.5 h-3.5" />
                  {isStoredPdfContent ? 'PDF Konumu' : (isFreeReadingMode || showMeasuredSpeed) ? 'Okuma Hızı' : 'Hedef Akış'}
                </div>
                <p className="mt-2 text-[24px] leading-none font-black text-[#194e62]">
                  {isStoredPdfContent
                    ? `${currentPdfPage} / ${storedPdfPageCount}`
                    : `${(isFreeReadingMode || showMeasuredSpeed) ? measuredWpm : settings.wordsPerMinute} kelime/dk`}
                </p>
                {(isFreeReadingMode || isStoredPdfContent || showMeasuredSpeed) && (
                  <p className="mt-2 text-[11px] font-semibold text-[#7b909b]">
                    {isStoredPdfContent ? 'Sayfa geçişi üstteki kontrollerden yapılır.' : `Hedef: ${settings.wordsPerMinute} kelime/dk`}
                  </p>
                )}
              </div>

              <button
                onClick={() => { void handlePrimaryAction(); }}
                disabled={(phase === 'review' && !reviewSubmitted && !hasAnsweredAllReviewQuestions) || isBusy}
                className={`mt-4 w-full h-12 rounded-lg text-white font-bold text-base transition-colors inline-flex items-center justify-center gap-2 ${phase === 'review' && !reviewSubmitted && !hasAnsweredAllReviewQuestions ? 'opacity-60 cursor-not-allowed' : ''
                  } ${isBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={getThemeChromeButtonStyle()}
              >
                {(primaryButtonLabel === 'Başlat' || primaryButtonLabel === 'Devam Et') && !isBusy && <Play className="w-4 h-4" />}
                {primaryButtonLabel === 'Durdur' && <Pause className="w-4 h-4" />}
                {isBusy ? 'İçerik Yükleniyor...' : primaryButtonLabel}
              </button>

              {hasReviewQuestions && (phase === 'paused' || phase === 'reading') && (
                <button
                  onClick={() => setPhase('review')}
                  className="mt-4 w-full h-12 rounded-lg font-bold text-base transition-colors inline-flex items-center justify-center bg-[#24465a] text-white hover:bg-[#1c394a]"
                >
                  Metni Bitirdim
                </button>
              )}

              {!hasReviewQuestions && (phase === 'reading' || phase === 'paused') && (
                <button
                  onClick={() => {
                    if (isFreeReadingMode) {
                      handleFinishFreeReading();
                      return;
                    }
                    void handleCloseExercise();
                  }}
                  className="mt-4 w-full h-12 rounded-lg bg-[#24465a] text-white font-bold text-base transition-colors inline-flex items-center justify-center hover:bg-[#1c394a]"
                >
                  Çalışmayı Bitir
                </button>
              )}
            </div>
          </aside>

          {isFullscreen && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] font-medium text-[#96a6af]">
              Tam ekrandan çıkmak için Esc tuşuna basın veya tıklayın
            </div>
          )}

          {settingsOpen && (
            <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-[4px] flex items-center justify-center">
              <div className="w-[920px] bg-white rounded-xl shadow-lg p-6 select-none">
                <div className="flex items-center justify-between mb-5">
                  <h4 className="text-xl font-bold" style={getThemedLabelStyle()}>Egzersiz Ayarları</h4>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setSettingsOpen(false)}>
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-7">
                  <div className="space-y-5">
                    <SettingsStepperRow
                      label="Gösterim Hızı"
                      value={`${shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute)} ms`}
                      onDecrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.max(0, currentIndex - 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                      onIncrease={() => {
                        const currentPaceMs = shared.wordsPerMinuteToClosestPaceMs(settings.wordsPerMinute);
                        const currentIndex = shared.PACE_MS_OPTIONS.findIndex((paceMs) => paceMs === currentPaceMs);
                        const nextPaceMs = shared.PACE_MS_OPTIONS[Math.min(shared.PACE_MS_OPTIONS.length - 1, currentIndex + 1)];
                        setSettings((prev) => ({
                          ...prev,
                          wordsPerMinute: shared.paceMsToWordsPerMinute(nextPaceMs),
                          paceMs: nextPaceMs,
                        }));
                      }}
                    />

                    <SettingsStepperRow
                      label="Metin Zorluğu"
                      value={settings.difficultyLevel}
                      onDecrease={() => {
                        const currentIndex = shared.DIFFICULTY_OPTIONS.findIndex((option) => option === settings.difficultyLevel);
                        const nextDifficulty = shared.DIFFICULTY_OPTIONS[Math.max(0, currentIndex - 1)];
                        setSettings((prev) => ({ ...prev, difficultyLevel: nextDifficulty }));
                      }}
                      onIncrease={() => {
                        const currentIndex = shared.DIFFICULTY_OPTIONS.findIndex((option) => option === settings.difficultyLevel);
                        const nextDifficulty = shared.DIFFICULTY_OPTIONS[Math.min(shared.DIFFICULTY_OPTIONS.length - 1, currentIndex + 1)];
                        setSettings((prev) => ({ ...prev, difficultyLevel: nextDifficulty }));
                      }}
                    />

                    {modeKey === 'eksiltili-metin' && (
                      <SettingsStepperRow
                        label="Maksimum Nokta"
                        value={`${effectiveIncompleteTextMaxDots}`}
                        onDecrease={() => setSettings((prev) => ({
                          ...prev,
                          incompleteTextMaxDots: Math.max(1, Math.min(3, Math.round(Number(prev.incompleteTextMaxDots) || 3)) - 1),
                        }))}
                        onIncrease={() => setSettings((prev) => ({
                          ...prev,
                          incompleteTextMaxDots: Math.min(3, Math.max(1, Math.round(Number(prev.incompleteTextMaxDots) || 3)) + 1),
                        }))}
                      />
                    )}

                    <SettingsToggleRow
                      label="Metronomu etkinleştir"
                      checked={settings.metronomeEnabled}
                      onChange={() => setSettings((prev) => ({ ...prev, metronomeEnabled: !prev.metronomeEnabled }))}
                    />

                    {settings.metronomeEnabled && (
                      <SettingsStepperRow
                        label="Metronom Hızı"
                        value={`${normalizeMetronomeBpm(settings.metronomeBpm)} BPM`}
                        onDecrease={() => setSettings((prev) => ({ ...prev, metronomeBpm: getAdjacentMetronomeBpm(prev.metronomeBpm, -1) }))}
                        onIncrease={() => setSettings((prev) => ({ ...prev, metronomeBpm: getAdjacentMetronomeBpm(prev.metronomeBpm, 1) }))}
                      />
                    )}

                    <SettingsToggleRow
                      label="Kronometreyi göster"
                      checked={settings.stopwatchEnabled}
                      onChange={() => setSettings((prev) => ({ ...prev, stopwatchEnabled: !prev.stopwatchEnabled }))}
                    />

                    <SettingsToggleRow
                      label="Başlamadan önce geriye say"
                      checked={settings.enableCountdown}
                      onChange={() => setSettings((prev) => ({ ...prev, enableCountdown: !prev.enableCountdown }))}
                    />
                  </div>

                  <div className="space-y-5 border-l border-[#edf2f5] pl-7">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Boyutu</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {FONT_SIZE_LABELS[settings.fontSize]}
                        </span>
                      </div>
                      <div className="grid grid-cols-5 gap-2">
                        {FONT_SIZE_OPTIONS.map((sizeOption) => {
                          const isActive = settings.fontSize === sizeOption.value;
                          return (
                            <button
                              key={sizeOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontSize: sizeOption.value }))}
                              className="h-10 rounded-lg border transition-colors"
                              style={getThemedOptionButtonStyle(isActive)}
                            >
                              <span className={`font-semibold ${sizeOption.preview}`}>{sizeOption.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-sm font-semibold" style={getThemedLabelStyle()}>Yazı Karakteri</p>
                      <div className="grid grid-cols-2 gap-2">
                        {FONT_FAMILY_OPTIONS.map((fontOption) => {
                          const isActive = settings.fontFamily === fontOption.value;
                          return (
                            <button
                              key={fontOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, fontFamily: fontOption.value }))}
                              className="h-10 rounded-lg border text-sm transition-colors"
                              style={{ ...getThemedOptionButtonStyle(isActive), fontFamily: fontOption.css }}
                            >
                              {fontOption.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold" style={getThemedLabelStyle()}>Vurgu Rengi</p>
                        <span className="rounded px-2 py-1 text-xs" style={getThemedBadgeStyle()}>
                          {ACCENT_COLOR_OPTIONS.find((colorOption) => colorOption.value === settings.accentColor)?.label || 'Varsayılan'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        {ACCENT_COLOR_OPTIONS.map((colorOption) => {
                          const isActive = settings.accentColor === colorOption.value;
                          return (
                            <button
                              key={colorOption.value}
                              onClick={() => setSettings((prev) => ({ ...prev, accentColor: colorOption.value }))}
                              className="h-7 w-7 rounded-full border-2 transition-transform hover:scale-105"
                              style={{ ...getThemedSwatchStyle(isActive, colorOption.hex), backgroundColor: colorOption.hex }}
                              title={colorOption.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {libraryOpen && (
            <div className="fixed inset-0 z-[125] bg-black/55 backdrop-blur-[4px] flex items-center justify-center p-4">
              <div className="w-full max-w-[980px] rounded-2xl bg-white shadow-lg">
                <div className="flex items-center justify-between border-b border-[#e9eff2] px-6 py-4">
                  <div>
                    <h4 className="text-xl font-bold text-[#234d5d]">{isFreeReadingMode ? 'Kütüphanem' : 'Metin Kütüphanesi'}</h4>
                    <p className="mt-1 text-sm text-[#738891]">
                      Bu egzersizde çalışmak istediğiniz metni seçin.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button className="text-gray-500 hover:text-gray-700" onClick={() => setLibraryOpen(false)}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="border-b border-[#eef3f5] px-6 py-4">
                  <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center">
                    <input
                      value={librarySearch}
                      onChange={(e) => setLibrarySearch(e.target.value)}
                      placeholder="Başlık, kategori veya içerikte ara"
                      className="h-11 rounded-xl border border-[#dce6eb] px-4 text-sm outline-none focus:border-[#8fb8c8]"
                    />
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'all', label: 'Tümü' },
                        { value: 'makale', label: 'Makale' },
                        { value: 'öykü', label: 'Öykü' },
                      ].map((option) => {
                        const isActive = libraryTextType === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={() => setLibraryTextType(option.value)}
                            className="rounded-xl border px-4 py-2 text-sm font-semibold transition-colors"
                            style={getThemedOptionButtonStyle(isActive)}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => { void fetchLibraryItems(); }}
                      className="h-11 rounded-xl border border-[#dce6eb] px-4 text-sm font-semibold text-[#335a6a] hover:bg-[#f7fbfd]"
                    >
                      Yenile
                    </button>
                  </div>
                </div>

                <div className="max-h-[68vh] overflow-y-auto px-6 py-5">
                  {uploadError && (
                    <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                      {uploadError}
                    </div>
                  )}
                  {libraryLoading ? (
                    <div className="flex min-h-[220px] flex-col items-center justify-center gap-4">
                      <div className="w-8 h-8 border-2 border-[#2f5563] border-t-transparent rounded-full animate-spin" />
                      <p className="text-sm font-semibold text-[#5a7380]">Kütüphane yükleniyor...</p>
                    </div>
                  ) : libraryItems.length === 0 ? (
                    <div className="flex min-h-[220px] flex-col items-center justify-center gap-4 text-center">
                      <Library className="w-10 h-10 text-[#a5b4bb]" />
                      <p className="text-sm font-semibold text-[#5a7380]">Bu filtrelerle eşleşen metin bulunamadı.</p>
                    </div>
                  ) : visibleLibraryLevelGroups.length > 0 ? (
                    <div className="space-y-7">
                      {visibleLibraryLevelGroups.map((levelGroup) => (
                        <section key={levelGroup.key} className="space-y-3">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <h5 className="text-[17px] font-black text-[#1e4c5d]">{levelGroup.label}</h5>
                              <p className="mt-1 text-[12px] font-semibold text-[#7a8e98]">
                                Seviyeye uygun, ölçme soruları hazırlanmış metinler
                              </p>
                            </div>
                            <span className="rounded-full border border-[#dce8ee] bg-[#fbfdfe] px-3 py-1.5 text-[12px] font-bold text-[#5d7681]">
                              {levelGroup.count}/{levelGroup.minimumRequired || 50} metin
                            </span>
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            {levelGroup.items.map(renderLibraryItemCard)}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {libraryItems.map(renderLibraryItemCard)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {showCompletionSummary && (
        <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-2xl rounded-[2rem] border border-[#dce8ee] bg-white p-8 shadow-[0_30px_90px_rgba(26,72,92,0.18)]">
            <div className="text-center">
              <div className="text-sm font-semibold uppercase tracking-[0.2em] text-[#7a8e98]">Egzersiz Özeti</div>
              <h3 className="mt-3 text-[2rem] font-black text-[#18485c]">
                {isFreeReadingMode ? 'Kütüphane Çalışması Kaydedildi' : 'Metin Değerlendirmesi Tamamlandı'}
              </h3>
              <p className="mt-3 text-[15px] leading-7 text-[#5c7480]">{reviewSummary}</p>
            </div>

            {isFreeReadingMode ? (
              <>
                <div className="mt-8 grid gap-4 md:grid-cols-4">
                  <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                    <div className="text-xs font-semibold text-[#7a8e98]">Süre</div>
                    <div className="mt-2 text-[28px] font-black text-[#18485c]">{timeLabel}</div>
                  </div>
                  <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                    <div className="text-xs font-semibold text-[#7a8e98]">Okuma Hızı</div>
                    <div className="mt-2 text-[28px] font-black text-[#18485c]">{measuredWpm} kelime/dk</div>
                  </div>
                  <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                    <div className="text-xs font-semibold text-[#7a8e98]">Hız Ölçeği</div>
                    <div className="mt-2 text-[28px] font-black text-[#18485c]">{freeReadingSpeedScore}/5</div>
                  </div>
                  <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                    <div className="text-xs font-semibold text-[#7a8e98]">Anlama Ölçeği</div>
                    <div className="mt-2 text-[28px] font-black text-[#18485c]">{freeReadingComprehensionScore}/5</div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5">
                  <div className="text-xs font-semibold text-[#7a8e98]">Notlar</div>
                  <p className="mt-2 text-sm leading-7 text-[#4f6772]">
                    {freeReadingNotes.trim() || 'Bu çalışma için not girilmedi.'}
                  </p>
                </div>
              </>
            ) : (
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                  <div className="text-xs font-semibold text-[#7a8e98]">Anlama</div>
                  <div className="mt-2 text-[28px] font-black text-[#18485c]">
                    {reviewCorrectCount} / {reviewQuestions.length}
                  </div>
                </div>
                <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                  <div className="text-xs font-semibold text-[#7a8e98]">Okuma Hızı</div>
                  <div className="mt-2 text-[28px] font-black text-[#18485c]">{measuredWpm} kelime/dk</div>
                </div>
                <div className="rounded-2xl border border-[#e2eaee] bg-[#fbfdfe] p-5 text-center">
                  <div className="text-xs font-semibold text-[#7a8e98]">Önerilen Hız</div>
                  <div className="mt-2 text-[28px] font-black text-[#18485c]">{recommendedWpm} kelime/dk</div>
                </div>
              </div>
            )}

            <button
              onClick={() => { void handleCloseExercise(); }}
              className="mt-8 h-12 w-full rounded-xl text-sm font-semibold text-white"
              style={getThemeChromeButtonStyle()}
            >
              Modlara Dön
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
